import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Send,
  Paperclip,
  Mic,
  Square,
  ArrowDown,
  Sparkles,
  Zap,
  Wrench,
  Trash2,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  MoreHorizontal,
  Hammer,
  FileText,
  Microscope,
  CalendarDays,
  Paperclip as PaperclipIcon,
  Sparkle,
  PlusCircle,
  X,
  Target,
  Compass,
  ArrowRight,
  Activity,
  Volume2,
  VolumeX,
  Radio,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeaders, getAuthToken, getUser, logout } from '../utils/auth';
import Layout from '../components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMAGE_MIME = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp']);
const IMAGE_TOKEN = /\[image:([a-zA-Z0-9-]+)\|([^\]]+)\]/g;

/* ------------------------------------------------------------------ */
/* Command intelligence — starters + specialized prompts              */
/* ------------------------------------------------------------------ */

const STARTER_BUILD = 'Build this with me: ';
const STARTER_DRAFT = 'Draft a ';
const STARTER_ANALYZE = 'Analyze this — tell me what I\'m missing: ';
const STARTER_PLAN = 'Plan my ';

const COMMAND_PROMPTS = {
  build: (payload) =>
    `[MODE OVERRIDE: This is a structured tool invocation, not casual chat. You MUST use markdown headers (##) and numbered lists exactly as specified. The usual "no headers" rule does not apply here.]

You are LEE operating in BUILD mode. Think like a builder and strategist. Turn the user's raw idea into a clear business/execution concept.

Respond in EXACTLY this markdown structure, with these six sections, in this exact order and with these exact section names:

## Core Idea
One sharp sentence defining what this is and why it exists. No adjectives for the sake of it.

## Best Use Case
Two to three sentences on the single scenario where this creates the most obvious, concrete value. Be specific — real person, real moment, real payoff.

## Target Market
Prose (no bullets). Describe the primary customer, the shape and rough size of the opportunity, and the strongest signal that this market is real and reachable.

## Monetization
Prose (no bullets). The primary revenue path with a concrete pricing hypothesis in dollars. Then one credible alternative path.

## Execution Steps
A numbered list of exactly 5 moves, ordered by leverage. Each step starts with an action verb and is specific enough to do this week.

## First Move
One sentence. The single highest-leverage action to take in the next 24 hours. Must be an action, not a thought.

Rules:
- No preamble. No "great idea". No "here's the breakdown".
- No closing summary, no sign-off, no motivational lines.
- Be direct. If the idea is weak, reflect that honestly inside Core Idea or Target Market.

USER'S IDEA:
${payload}`,

  analyze: (payload) =>
    `[MODE OVERRIDE: This is a structured tool invocation, not casual chat. You MUST use markdown headers (##), bullet lists, and numbered lists exactly as specified. The usual "no headers" rule does not apply here.]

You are LEE operating in ANALYZE mode. Think like a sharp, honest evaluator — not a generic assistant. Break down the user's idea, plan, or message with precision.

Respond in EXACTLY this markdown structure, with these five sections, in this exact order and with these exact section names:

## What's Strong
Three bullet points (use "- "). What is genuinely, materially strong. No flattery, no padding, no "great thinking".

## What's Weak
Three bullet points (use "- "). The real cracks — flawed logic, soft assumptions, wishful thinking, hidden costs.

## What's Missing
Three bullet points (use "- "). What the user has not yet considered — unstated assumptions, blind spots, second-order effects.

## Risks
Three bullet points (use "- "). Concrete failure modes and how they would actually manifest. No vague risks like "market could change".

## Improvement Path
A numbered list of exactly 3 concrete moves to make this materially better. Each starts with an action verb and is specific enough to act on now.

Rules:
- No preamble. No "let's dive in".
- Don't hedge. If it's weak, say it's weak.
- No fence-sitting ("on one hand... on the other hand").
- No closing summary, no sign-off.

SUBJECT:
${payload}`,

  draft: (payload) =>
    `[MODE OVERRIDE: This is a structured tool invocation, not casual chat. You MUST output a complete, ready-to-use document in markdown. Use headers and formatting as needed. The usual "no headers" rule does not apply here.]

You are LEE operating in DRAFT mode. Produce a clean, polished, ready-to-use document that the user can copy and send with minimal editing.

Follow this structure:

## Title
A single "# Title Line" at the very top of the document, matching the document type (memo, pitch, email, brief, proposal, letter, post, press release, etc.).

## Main Body
The full body of the document, well organized with "## Section" headings where the format calls for them. Use short, active sentences, plain language, and specific concrete details. If key details are missing, use sensible placeholders in [SQUARE BRACKETS].

## Optional Closing
Include ONLY if the document type requires one:
- For emails/letters → a sign-off and signature line with a [Your Name] placeholder.
- For pitches/proposals/marketing → a single clear Call To Action line.
- For memos/briefs → end cleanly with no closing line at all.

Rules:
- The entire response IS the document. No preamble ("here's your draft"), no commentary outside the document, no meta-explanation.
- No emojis.
- Do not label the sections literally as "Title / Main Body / Optional Closing" in the output — those labels describe what to include; the actual document uses real titles and headings.

USER'S REQUEST:
${payload}`,

  plan: (payload) =>
    `[MODE OVERRIDE: This is a structured tool invocation, not casual chat. You MUST use markdown headers (##), numbered lists, and bullets exactly as specified. The usual "no headers" rule does not apply here.]

You are LEE operating in PLAN mode. Think like an execution planner. Turn the user's goal into a phased, actionable sequence.

Respond in EXACTLY this markdown structure, with these five sections, in this exact order and with these exact section names:

## Objective
One line — the real target, sharpened. If the user's goal is vague, tighten it here.

## Phase 1
A numbered list of 3 to 5 steps that happen FIRST (foundation / setup / proof). Each step starts with an action verb, is specific enough to execute this week, and ends with a rough time estimate in parentheses — e.g. "(2h)", "(half day)", "(3 days)".

## Phase 2
A numbered list of 3 to 5 steps that happen AFTER Phase 1 (build / launch / expand). Same format as Phase 1.

## Phase 3
A numbered list of 2 to 4 steps that happen LAST (scale / refine / lock-in). Same format as Phase 1.

## Immediate Next Step
One sentence. The single action to take in the next few hours. Must be an action, not a decision.

Rules:
- No preamble. No "great goal". No "let's plan this out".
- Use direct verbs. No "consider", "think about", "explore".
- No closing summary or motivational line.
- Each step is specific enough that a reasonable person could start it today.

USER'S GOAL:
${payload}`,
};

// Detect a structured command from raw input text.
// Returns { key, payload } or null.
const detectCommand = (raw) => {
  const text = (raw || '').replace(/^\s+/, '');
  const tests = [
    { key: 'build', prefix: STARTER_BUILD },
    { key: 'analyze', prefix: STARTER_ANALYZE },
    { key: 'draft', prefix: STARTER_DRAFT },
    { key: 'plan', prefix: STARTER_PLAN },
  ];
  for (const t of tests) {
    if (text.startsWith(t.prefix)) {
      const payload = text.slice(t.prefix.length).trim();
      return payload.length > 0 ? { key: t.key, payload } : null;
    }
  }
  return null;
};

// Common English words to ignore when matching goal titles to user message.
const GOAL_STOPWORDS = new Set([
  'the','and','for','with','this','that','from','have','will','what','when','where',
  'your','about','into','their','them','they','then','than','some','these','those',
  'goal','goals','project','projects','task','tasks','idea','ideas','thing','things',
  'plan','plans','make','take','need','want','like','just','very','more','most',
  'first','last','next','prev','time','today','tomorrow','week','month','year',
  'lee','daily','weekly','monthly',
]);

// Trigger phrases that indicate goal-relevant intent
// (planning / focus / execution / progress questions).
const GOAL_TRIGGER_PATTERNS = [
  /\bwhat (should|do) i\b/i,
  /\bwhat'?s next\b/i,
  /\bwhat (to|should i) (do|work on|focus|tackle)\b/i,
  /\b(today|tomorrow|this week|next week|right now)\b/i,
  /\b(focus|priorit|priorities)\b/i,
  /\bnext step\b/i,
  /\b(my )?(goal|project|priority|priorities|plan|roadmap)\b/i,
  /\bworking on\b/i,
  /\b(strategy|strategic|execute|execution)\b/i,
  /\b(progress|status update|where am i)\b/i,
  /\b(ship|launch|build|finish|complete) (it|this|my|the)\b/i,
  /\bhow (am|do|should) i\b/i,
];

// Pick up to 2 goals from the active list that look relevant to `text`.
// Heuristic order:
//   1. Strong: goal title shares a significant non-stopword with the message
//   2. Weak:   message matches a generic "what should I do" trigger phrase
//              → fall back to the highest-priority active goal
// Returns [] when nothing is clearly relevant — chat then runs normally.
const pickRelevantGoals = (text, goals) => {
  if (!text || !goals || goals.length === 0) return [];
  const t = text.toLowerCase();

  // Strong match: significant word overlap between message and goal title.
  const strong = [];
  for (const g of goals) {
    const titleWords = (g.title || '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((w) => w.length > 3 && !GOAL_STOPWORDS.has(w));
    const hit = titleWords.find((w) => t.includes(w));
    if (hit) strong.push(g);
    if (strong.length >= 2) break;
  }
  if (strong.length > 0) return strong.slice(0, 2);

  // Weak match: trigger phrase only — surface top priority active goal.
  const triggered = GOAL_TRIGGER_PATTERNS.some((re) => re.test(t));
  if (!triggered) return [];

  const rank = { critical: 0, high: 1, medium: 2, low: 3 };
  const ranked = [...goals].sort((a, b) => {
    const r = (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9);
    if (r !== 0) return r;
    return (a.deadline || 'z').localeCompare(b.deadline || 'z');
  });
  return ranked.slice(0, 1); // be conservative — only 1 on weak match
};

// Format a small "[LEE memory: ...]" block to inject into the prompt.
const buildGoalContextBlock = (goals) => {
  if (!goals || goals.length === 0) return '';
  const lines = goals.map((g) => {
    const prio = (g.priority || 'medium').toUpperCase();
    const due = g.deadline ? `; due ${g.deadline}` : '';
    const next = g.next_step ? `; next: ${g.next_step}` : '';
    return `• ${g.title} (${prio}${next}${due})`;
  });
  return (
    `[LEE memory — user is actively tracking ${goals.length === 1 ? 'this goal' : 'these goals'}. ` +
    `Reference naturally only if it genuinely helps the user's question. ` +
    `Do not list mechanically, do not force it, and never mention more than 2:\n` +
    lines.join('\n') +
    `]\n\n`
  );
};

// Truncate file text safely for prompt inclusion.
// Caps per-file at PER_FILE_MAX chars; for longer files, takes head + tail so
// LEE still sees intro context and conclusion (best for summary/gap analysis).
const PER_FILE_MAX = 8000;
const truncateForPrompt = (text) => {
  if (!text) return '';
  const t = String(text);
  if (t.length <= PER_FILE_MAX) return t;
  const half = Math.floor((PER_FILE_MAX - 60) / 2);
  return (
    t.slice(0, half) +
    `\n\n…[middle ${t.length - PER_FILE_MAX + 60} chars truncated for prompt size]…\n\n` +
    t.slice(t.length - half)
  );
};

// Build the "[LEE — attached files...]" block plus per-file content blocks.
// Total payload is capped at TOTAL_MAX chars; oldest files truncate first.
const TOTAL_MAX = 30000;
const buildFileContextBlock = (textFiles) => {
  if (!textFiles || textFiles.length === 0) return '';
  // Pre-truncate each file
  const pieces = textFiles.map((a) => {
    const body = truncateForPrompt(a.text || '');
    const header =
      `[FILE: ${a.name || 'untitled'}` +
      (a.docType ? ` (${a.docType.toUpperCase()})` : '') +
      (a.pages ? `, ${a.pages} page${a.pages === 1 ? '' : 's'}` : '') +
      (a.rowCount ? `, ${a.rowCount} rows` : '') +
      `]`;
    return { header, body };
  });
  // Enforce total cap across all files (drop oldest content if needed)
  let runningTotal = pieces.reduce((s, p) => s + p.header.length + p.body.length + 12, 0);
  while (runningTotal > TOTAL_MAX && pieces.length > 0) {
    // Shorten the largest body first
    const idx = pieces.reduce((m, p, i) => (p.body.length > pieces[m].body.length ? i : m), 0);
    const overhead = runningTotal - TOTAL_MAX;
    const newLen = Math.max(500, pieces[idx].body.length - overhead - 100);
    pieces[idx].body = pieces[idx].body.slice(0, newLen) + '\n…[further truncated]…';
    runningTotal = pieces.reduce((s, p) => s + p.header.length + p.body.length + 12, 0);
  }
  const fileBlocks = pieces
    .map((p) => `${p.header}\n<<<\n${p.body}\n>>>`)
    .join('\n\n');
  const instr =
    `[LEE — the user has attached ${textFiles.length} file${textFiles.length === 1 ? '' : 's'} for this turn. ` +
    `Treat their content as primary context. Depending on what the user asked, do the most useful one or two of: ` +
    `(a) summarize concisely, (b) extract key points, (c) answer their question using the file, ` +
    `(d) identify concrete next steps, (e) flag risks, gaps, or missing pieces. ` +
    `Structure the answer with short headers or bullets only when it actually helps clarity. ` +
    `Do not just acknowledge the file — use it. ` +
    `If the user did not ask a specific question, default to a tight summary plus key points plus 1-3 suggested next steps.]\n\n`;
  return instr + fileBlocks + '\n\n';
};

/* ------------------------------------------------------------------ */
/* Slash command palette definitions                                  */
/* ------------------------------------------------------------------ */

const COMMANDS = [
  {
    key: 'new',
    label: '/new',
    icon: PlusCircle,
    hint: 'Start a fresh conversation',
    starter: null, // triggers startNewChat
  },
  {
    key: 'summary',
    label: '/summary',
    icon: Sparkle,
    hint: 'One-line summary of this thread',
    starter: null, // triggers generateSummary
  },
  {
    key: 'build',
    label: '/build',
    icon: Hammer,
    hint: 'Structured build brief — idea, market, monetization, execution',
    starter: STARTER_BUILD,
  },
  {
    key: 'draft',
    label: '/draft',
    icon: FileText,
    hint: 'Clean, formatted document (memo, pitch, email, brief)',
    starter: STARTER_DRAFT,
  },
  {
    key: 'analyze',
    label: '/analyze',
    icon: Microscope,
    hint: 'Pressure-test: strengths, weaknesses, gaps, improvements',
    starter: STARTER_ANALYZE,
  },
  {
    key: 'plan',
    label: '/plan',
    icon: CalendarDays,
    hint: 'Step-by-step actionable plan with first 24 hours',
    starter: STARTER_PLAN,
  },
  {
    key: 'goals',
    label: '/goals',
    icon: Target,
    hint: 'Show my active goals',
    starter: null, // triggers showGoals
  },
  {
    key: 'focus',
    label: '/focus',
    icon: Compass,
    hint: 'Generate my current focus brief',
    starter: null, // triggers showFocus
  },
  {
    key: 'next',
    label: '/next',
    icon: ArrowRight,
    hint: 'Suggest the next best action from my active goals',
    starter: null, // triggers suggestNextAction
  },
  {
    key: 'progress',
    label: '/progress',
    icon: Activity,
    hint: 'Update progress on one of my active goals',
    starter: null, // triggers showProgressPicker
  },
  {
    key: 'file',
    label: '/file',
    icon: PaperclipIcon,
    hint: 'Attach a file to this message',
    starter: null, // triggers file picker instead
  },
];


/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const groupByDate = (items) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86400000;
  const last7 = today - 6 * 86400000;
  const last30 = today - 29 * 86400000;

  const buckets = { Today: [], Yesterday: [], 'Previous 7 days': [], 'Previous 30 days': [], Older: [] };
  items.forEach((c) => {
    const t = new Date(c.updated_at || c.created_at || 0).getTime();
    if (t >= today) buckets.Today.push(c);
    else if (t >= yesterday) buckets.Yesterday.push(c);
    else if (t >= last7) buckets['Previous 7 days'].push(c);
    else if (t >= last30) buckets['Previous 30 days'].push(c);
    else buckets.Older.push(c);
  });
  return buckets;
};

const renderInline = (text) => {
  // Minimal inline rendering: **bold**, `code`
  const parts = [];
  let i = 0;
  const regex = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`)/g;
  let m;
  let last = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('**')) {
      parts.push(
        <strong key={`b-${i++}`} className="text-white font-semibold">
          {token.slice(2, -2)}
        </strong>
      );
    } else if (token.startsWith('*')) {
      parts.push(
        <em key={`i-${i++}`} className="text-zinc-300 italic">
          {token.slice(1, -1)}
        </em>
      );
    } else {
      parts.push(
        <code key={`c-${i++}`} className="bg-white/[0.06] text-amber-300 px-1.5 py-0.5 rounded text-[12px] font-mono">
          {token.slice(1, -1)}
        </code>
      );
    }
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

// Inline HTML/CSS/JS preview component — renders fenced ```html blocks in an
// iframe so LEE can produce full landing pages, web apps, prototypes that the
// user can SEE live, not just read code.
const CodePreview = ({ code, lang }) => {
  const [showPreview, setShowPreview] = useState(true);
  const [copied, setCopied] = useState(false);
  const isHtml = lang === 'html' || /^\s*<!doctype|^\s*<html|^\s*<body|^\s*<div/i.test(code);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* noop */ }
  };
  const handleOpen = () => {
    const blob = new Blob([code], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return (
    <div className="my-3 rounded-xl border border-white/[0.08] bg-zinc-950/40 overflow-hidden max-w-full" data-testid={`code-block-${lang || 'plain'}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06] bg-white/[0.02]">
        <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-zinc-500">
          {lang || 'code'}{isHtml ? ' · preview live' : ''}
        </span>
        <div className="flex items-center gap-1">
          {isHtml && (
            <>
              <button
                onClick={() => setShowPreview((p) => !p)}
                data-testid="toggle-preview-btn"
                className="px-2 py-1 text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-400 hover:text-amber-300 hover:bg-white/[0.04] rounded transition-colors"
              >
                {showPreview ? 'Hide preview' : 'Show preview'}
              </button>
              <button
                onClick={handleOpen}
                data-testid="open-preview-btn"
                className="px-2 py-1 text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-400 hover:text-amber-300 hover:bg-white/[0.04] rounded transition-colors"
              >
                Open in new tab
              </button>
            </>
          )}
          <button
            onClick={handleCopy}
            data-testid="copy-code-btn"
            className="px-2 py-1 text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-400 hover:text-amber-300 hover:bg-white/[0.04] rounded transition-colors"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      {isHtml && showPreview && (
        <iframe
          data-testid="code-preview-iframe"
          title="LEE preview"
          srcDoc={code}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          className="w-full bg-white"
          style={{ height: 480, border: 0 }}
        />
      )}
      <pre className="overflow-x-auto px-4 py-3 text-[12px] text-zinc-300 font-mono leading-relaxed max-h-[360px]">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const renderMessage = (content) => {
  if (!content) return null;
  // First pass: pull out fenced code blocks so we can render them as
  // CodePreview components instead of plain text lines.
  const parts = [];
  const re = /```([a-zA-Z0-9_+-]*)\s*\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = re.exec(content)) !== null) {
    if (m.index > lastIndex) {
      parts.push({ kind: 'text', text: content.slice(lastIndex, m.index), key: key++ });
    }
    parts.push({ kind: 'code', lang: (m[1] || '').toLowerCase(), code: m[2], key: key++ });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < content.length) {
    parts.push({ kind: 'text', text: content.slice(lastIndex), key: key++ });
  }
  if (parts.length === 0) {
    parts.push({ kind: 'text', text: content, key: 0 });
  }
  return parts.map((p) => {
    if (p.kind === 'code') {
      return <CodePreview key={p.key} code={p.code} lang={p.lang} />;
    }
    return <RenderTextLines key={p.key} text={p.text} />;
  });
};

const RenderTextLines = ({ text }) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.trim() === '') return <div key={i} className="h-3" />;

    // ![alt](url) — generated image, render inline
    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)\s*$/);
    if (imgMatch) {
      const alt = imgMatch[1] || 'Generated image';
      let src = imgMatch[2];
      // Backend-relative URL — prefix with REACT_APP_BACKEND_URL.
      if (src.startsWith('/api/')) {
        src = `${process.env.REACT_APP_BACKEND_URL || ''}${src}`;
      }
      return (
        <div key={i} className="my-3" data-testid="generated-image">
          <a href={src} target="_blank" rel="noreferrer" className="block group">
            <img
              src={src}
              alt={alt}
              className="rounded-xl border border-white/[0.08] max-w-full max-h-[520px] shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)] group-hover:opacity-95 transition-opacity"
              loading="lazy"
            />
            {alt && alt !== 'Generated image' && (
              <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-zinc-500 mt-1.5">
                {alt}
              </div>
            )}
          </a>
        </div>
      );
    }

    // # Title
    if (/^#\s+/.test(line)) {
      return (
        <h3
          key={i}
          className="text-[17px] font-semibold text-white tracking-tight leading-snug mt-1 mb-1.5"
        >
          {renderInline(line.replace(/^#\s+/, ''))}
        </h3>
      );
    }
    // ## Section
    if (/^##\s+/.test(line)) {
      return (
        <div
          key={i}
          className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400/80 mt-4 mb-1.5"
        >
          {line.replace(/^##\s+/, '')}
        </div>
      );
    }
    // ### Sub-section
    if (/^###\s+/.test(line)) {
      return (
        <div
          key={i}
          className="text-[12px] font-semibold text-zinc-300 mt-2 mb-1"
        >
          {renderInline(line.replace(/^###\s+/, ''))}
        </div>
      );
    }
    // Numbered list: "1. text"
    const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numMatch) {
      return (
        <div key={i} className="flex gap-3 text-[15px] text-zinc-200 leading-[1.65] my-0.5">
          <span className="text-amber-400/70 font-mono text-[12px] tabular-nums pt-[3px] flex-shrink-0 w-5">
            {numMatch[1]}.
          </span>
          <span className="flex-1">{renderInline(numMatch[2])}</span>
        </div>
      );
    }
    // Bullet: "- text" or "* text"
    if (/^[-*]\s+/.test(line)) {
      return (
        <div key={i} className="flex gap-3 text-[15px] text-zinc-200 leading-[1.65] my-0.5">
          <span className="text-amber-400/60 pt-[3px] flex-shrink-0">•</span>
          <span className="flex-1">{renderInline(line.replace(/^[-*]\s+/, ''))}</span>
        </div>
      );
    }

    return (
      <p key={i} className="text-[15px] text-zinc-200 leading-[1.72] tracking-[-0.005em]">
        {renderInline(line)}
      </p>
    );
  });
};

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

const TranscendentChat = () => {
  const navigate = useNavigate();
  const user = getUser();

  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 768
  );
  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');

  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Slash command palette
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [paletteIdx, setPaletteIdx] = useState(0);

  // Summary banner
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Image URL cache (file_id -> objectURL or data URL) for rendering bubbles
  const [imageUrls, setImageUrls] = useState({});

  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  // Cache of active goals for ambient awareness in normal chat replies.
  // Refreshed on mount, after /progress updates, and on /goals + /progress invocations.
  const activeGoalsRef = useRef([]);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  // ============================================================
  // Phase 1 ONE LEE — Voice response (V1-V4)
  // V1: persisted toggle (default OFF)
  // V2: speak final assistant text via /api/voice/tts (OpenAI, voice=onyx)
  // V3: stop any in-flight audio on new turn / unmount
  // V4: explicit "Stop Speaking" control while audio is playing
  // ============================================================
  const [speakResponses, setSpeakResponses] = useState(() => {
    try {
      // U1: default ON for first-time users; respect explicit user choice afterwards.
      const v = localStorage.getItem('lee_speak_responses');
      if (v === null) return true;
      return v === '1';
    } catch {
      return true;
    }
  });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef(null);

  // ---- Streaming TTS queue (PROGRESSIVE PLAYBACK via MediaSource) ----
  // One MediaSource + SourceBuffer per turn. All sentence chunks append into
  // the SAME SourceBuffer in 'sequence' mode → browser plays continuously
  // as bytes flow in, gap-less.
  //
  // Time-to-first-word collapses to ~600-900 ms: the browser starts decoding
  // the moment ~32KB of mp3 has arrived, not after the full file completes.
  const TTS_VOICE = 'ash';
  const TTS_MODEL = 'tts-1-hd';

  const msRef = useRef(null);        // MediaSource
  const sbRef = useRef(null);        // SourceBuffer
  const audioElRef = useRef(null);   // <audio> playing the MediaSource
  const objectUrlRef = useRef(null); // ms blob URL (for cleanup)
  const appendQueueRef = useRef([]); // pending Uint8Array bytes
  const appendingRef = useRef(false);
  const chunkOrderRef = useRef(0);   // monotonic chunk index
  const lastEnqueuedIndexRef = useRef(-1); // highest index actually appended
  const pendingByIndexRef = useRef(new Map()); // index -> bytes for out-of-order
  const ttsConsumedRef = useRef(0);
  const ttsStreamDoneRef = useRef(false);
  const ttsAbortedRef = useRef(false);
  const audioCtxRef = useRef(null);  // kept for the OscillatorNode ack only

  const _getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return null;
      audioCtxRef.current = new Ctor();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  }, []);

  const _msSupported = () => {
    try {
      return (
        typeof window.MediaSource !== 'undefined' &&
        window.MediaSource.isTypeSupported('audio/mpeg')
      );
    } catch {
      return false;
    }
  };

  const _drainAppendQueue = useCallback(() => {
    if (appendingRef.current) return;
    const sb = sbRef.current;
    if (!sb || appendQueueRef.current.length === 0) return;
    const next = appendQueueRef.current.shift();
    if (!next || next.byteLength === 0) {
      _drainAppendQueue();
      return;
    }
    try {
      appendingRef.current = true;
      sb.appendBuffer(next);
    } catch {
      appendingRef.current = false;
    }
  }, []);

  const _openMediaSource = useCallback(() => {
    if (msRef.current) return msRef.current;
    if (!_msSupported()) return null;
    const ms = new MediaSource();
    const url = URL.createObjectURL(ms);
    const audio = new Audio();
    audio.preload = 'auto';
    audio.autoplay = true;
    audio.src = url;
    audio.onended = () => {
      // The single-MediaSource stream ended naturally — mark not speaking
      // only when the SSE stream is also done (i.e. no more chunks coming).
      if (ttsStreamDoneRef.current) {
        setIsSpeaking(false);
      }
    };
    audio.onerror = () => {
      setIsSpeaking(false);
    };
    audioRef.current = audio;
    audioElRef.current = audio;
    msRef.current = ms;
    objectUrlRef.current = url;
    setIsSpeaking(true);

    ms.addEventListener('sourceopen', () => {
      try {
        const sb = ms.addSourceBuffer('audio/mpeg');
        sb.mode = 'sequence';
        sb.addEventListener('updateend', () => {
          appendingRef.current = false;
          _drainAppendQueue();
        });
        sbRef.current = sb;
        _drainAppendQueue();
      } catch {
        /* noop */
      }
    });
    return ms;
  }, [_drainAppendQueue, setIsSpeaking]);

  const _appendBytes = useCallback((bytes) => {
    if (!bytes || bytes.byteLength === 0) return;
    appendQueueRef.current.push(bytes);
    _drainAppendQueue();
  }, [_drainAppendQueue]);

  const _endMediaSource = useCallback(() => {
    const ms = msRef.current;
    if (!ms) return;
    const tryEnd = () => {
      try {
        if (
          ms.readyState === 'open' &&
          !appendingRef.current &&
          appendQueueRef.current.length === 0
        ) {
          ms.endOfStream();
        } else {
          setTimeout(tryEnd, 80);
        }
      } catch {
        /* noop */
      }
    };
    tryEnd();
  }, []);

  // Fetch /voice/tts?stream=1 and append bytes to the shared SourceBuffer as
  // they arrive. Preserves order across chunks via index gating.
  const _streamChunkInto = useCallback(async (text, index) => {
    const safe = (text || '').slice(0, 4000);
    if (!safe.trim()) return;
    try {
      const resp = await fetch(`${API}/voice/tts?stream=1`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: safe, voice: TTS_VOICE, model: TTS_MODEL }),
      });
      if (!resp.ok || !resp.body) return;
      _openMediaSource();
      const reader = resp.body.getReader();
      const buf = [];
      // Read all bytes for this chunk first; then we'll append in order.
      // (Reading concurrently lets multiple chunks fetch in parallel — server
      // and OpenAI both stream — so chunk N+1 download overlaps chunk N play.)
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (ttsAbortedRef.current) {
          try { reader.cancel(); } catch { /* noop */ }
          return;
        }
        if (value && value.byteLength) buf.push(value);
      }
      // Wait until our index is next.
      while (lastEnqueuedIndexRef.current + 1 !== index) {
        if (ttsAbortedRef.current) return;
        pendingByIndexRef.current.set(index, buf);
        await new Promise((r) => setTimeout(r, 30));
        // If our turn came up while we were waiting, drop the pending entry.
        if (lastEnqueuedIndexRef.current + 1 === index) {
          pendingByIndexRef.current.delete(index);
          break;
        }
      }
      if (ttsAbortedRef.current) return;
      // Append our bytes.
      for (const piece of buf) _appendBytes(piece);
      lastEnqueuedIndexRef.current = index;
      // Drain anything pending that's now ready (index+1, index+2, ...).
      while (pendingByIndexRef.current.has(lastEnqueuedIndexRef.current + 1)) {
        const k = lastEnqueuedIndexRef.current + 1;
        const pieces = pendingByIndexRef.current.get(k);
        pendingByIndexRef.current.delete(k);
        for (const piece of pieces) _appendBytes(piece);
        lastEnqueuedIndexRef.current = k;
      }
    } catch {
      /* noop */
    }
  }, [_openMediaSource, _appendBytes]);

  const _ttsEnqueueChunk = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || ttsAbortedRef.current) return;
    const index = chunkOrderRef.current;
    chunkOrderRef.current += 1;
    await _streamChunkInto(trimmed, index);
  }, [_streamChunkInto]);

  const _ttsReset = useCallback(() => {
    ttsAbortedRef.current = true;
    if (audioElRef.current) {
      try { audioElRef.current.pause(); } catch { /* noop */ }
      try { audioElRef.current.src = ''; } catch { /* noop */ }
    }
    if (objectUrlRef.current) {
      try { URL.revokeObjectURL(objectUrlRef.current); } catch { /* noop */ }
    }
    audioElRef.current = null;
    msRef.current = null;
    sbRef.current = null;
    objectUrlRef.current = null;
    appendQueueRef.current = [];
    pendingByIndexRef.current = new Map();
    appendingRef.current = false;
    chunkOrderRef.current = 0;
    lastEnqueuedIndexRef.current = -1;
    ttsConsumedRef.current = 0;
    ttsStreamDoneRef.current = false;
    audioRef.current = null;
    setIsSpeaking(false);
  }, [setIsSpeaking]);

  // Strip markdown image syntax + IMAGE_TOKEN so TTS doesn't read URLs aloud.
  const _ttsClean = useCallback((text) => {
    if (!text) return '';
    return text
      .replace(/!\[[^\]]*\]\([^)]+\)/g, ' ')
      .replace(IMAGE_TOKEN, ' ');
  }, []);

  // Smarter sentence detection (unchanged from Sprint A).
  const _splitSentences = (text) => {
    if (!text) return { sentences: [], tail: '' };
    const re = /([\s\S]+?(?:[.!?…]+["')\]]*|[;—\n]))(?:\s+|$)/g;
    const sentences = [];
    let lastEnd = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      const sentence = m[1].trim();
      const endIdx = m.index + m[0].length;
      const isHard = /[.!?…]["')\]]*$/.test(sentence);
      const len = sentence.length;
      const shouldShip = isHard
        ? (len >= 6 || sentences.length > 0)
        : len >= 30;
      if (shouldShip) {
        sentences.push(sentence);
        lastEnd = endIdx;
      }
    }
    return { sentences, tail: text.slice(lastEnd) };
  };

  const ttsFeedStreaming = useCallback(
    (acc) => {
      if (!speakResponses) return;
      const cleaned = _ttsClean(acc);
      if (cleaned.length <= ttsConsumedRef.current) return;
      const fresh = cleaned.slice(ttsConsumedRef.current);
      const { sentences, tail } = _splitSentences(fresh);
      if (
        sentences.length === 0 &&
        ttsConsumedRef.current === 0 &&
        tail.length > 0
      ) {
        const commaIdx = tail.indexOf(', ');
        if (commaIdx >= 15 && commaIdx < 60) {
          const prefix = tail.slice(0, commaIdx + 1).trim();
          if (prefix.length >= 15) {
            _ttsEnqueueChunk(prefix);
            ttsConsumedRef.current += commaIdx + 2;
            return;
          }
        }
        return;
      }
      if (sentences.length === 0) return;
      for (const s of sentences) _ttsEnqueueChunk(s);
      ttsConsumedRef.current = cleaned.length - tail.length;
    },
    [speakResponses, _ttsEnqueueChunk, _ttsClean]
  );

  const ttsFlushOnDone = useCallback(
    (fullText) => {
      if (!speakResponses) return;
      const cleaned = _ttsClean(fullText);
      const tail = cleaned.slice(ttsConsumedRef.current).trim();
      if (tail) {
        _ttsEnqueueChunk(tail);
        ttsConsumedRef.current = cleaned.length;
      }
      ttsStreamDoneRef.current = true;
      // Close out the MediaSource after the last chunk lands.
      setTimeout(() => _endMediaSource(), 150);
    },
    [speakResponses, _ttsEnqueueChunk, _ttsClean, _endMediaSource]
  );

  const stopSpeaking = useCallback(() => {
    _ttsReset();
  }, [_ttsReset]);

  const toggleSpeakResponses = useCallback(() => {
    setSpeakResponses((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('lee_speak_responses', next ? '1' : '0');
      } catch {
        /* noop */
      }
      if (!next) _ttsReset();
      return next;
    });
  }, [_ttsReset]);

  const playAssistantSpeech = useCallback(
    async (text) => {
      if (!text || !text.trim()) return;
      _ttsReset();
      ttsAbortedRef.current = false;
      ttsStreamDoneRef.current = false;
      const cleaned = _ttsClean(text);
      const { sentences, tail } = _splitSentences(cleaned);
      const chunks = [...sentences];
      if (tail.trim()) chunks.push(tail.trim());
      if (chunks.length === 0) chunks.push(cleaned);
      for (const c of chunks) _ttsEnqueueChunk(c);
      ttsStreamDoneRef.current = true;
      setTimeout(() => _endMediaSource(), 150);
    },
    [_ttsReset, _ttsEnqueueChunk, _ttsClean, _endMediaSource]
  );

  // Pre-warm the backend connection + AudioContext when entering Voice Mode.
  const _ttsWarmup = useCallback(async () => {
    try {
      await axios.get(`${API}/voice/warmup`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        timeout: 5000,
      });
    } catch {
      /* noop */
    }
    _getAudioCtx();
  }, [_getAudioCtx]);

  // -------- P2.2 Option 2: 80ms acoustic micro-ack --------
  // Fires the instant the user's final transcript is captured, before LLM
  // round-trip, so the user *feels* LEE heard them. Very subtle: 600Hz tone
  // shaped with fast attack + decay envelope at -30 dB. ON by default.
  const [acousticAckEnabled, setAcousticAckEnabled] = useState(() => {
    try {
      const v = localStorage.getItem('lee_acoustic_ack');
      return v === null ? true : v === '1';
    } catch {
      return true;
    }
  });

  const _playAcousticAck = useCallback(() => {
    if (!acousticAckEnabled) return;
    const ctx = _getAudioCtx();
    if (!ctx) return;
    try {
      const t0 = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(620, t0);
      // -30 dB peak, shaped: 5ms attack, 75ms decay
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.032, t0 + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.08);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.09);
    } catch {
      /* noop */
    }
  }, [acousticAckEnabled, _getAudioCtx]);

  // -------- P2.2 Option 3: Voice acknowledgement filler --------
  // OFF by default. When ON, picks a short filler ("Mmhm", "Got it", "One sec")
  // and ships it into the TTS queue immediately so LEE has *something* spoken
  // before the actual reply begins. Uses the existing gap-less audio queue.
  const [voiceAckEnabled, setVoiceAckEnabled] = useState(() => {
    try {
      return localStorage.getItem('lee_voice_ack') === '1';
    } catch {
      return false;
    }
  });
  const _VOICE_ACK_PHRASES = ['Mmhm.', 'Got it.', 'One sec.', 'Right.', 'Okay.'];

  const _playVoiceAck = useCallback(() => {
    if (!voiceAckEnabled) return;
    const phrase = _VOICE_ACK_PHRASES[
      Math.floor(Math.random() * _VOICE_ACK_PHRASES.length)
    ];
    // Reset the stream-done flag so the actual reply can still flow through.
    ttsAbortedRef.current = false;
    ttsStreamDoneRef.current = false;
    _ttsEnqueueChunk(phrase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceAckEnabled, _ttsEnqueueChunk]);

  const toggleAcousticAck = useCallback(() => {
    setAcousticAckEnabled((p) => {
      const next = !p;
      try {
        localStorage.setItem('lee_acoustic_ack', next ? '1' : '0');
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);

  const toggleVoiceAck = useCallback(() => {
    setVoiceAckEnabled((p) => {
      const next = !p;
      try {
        localStorage.setItem('lee_voice_ack', next ? '1' : '0');
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);

  // V3: stop audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.src = '';
        } catch {
          /* noop */
        }
        audioRef.current = null;
      }
    };
  }, []);

  /* ---------------- Effects ---------------- */

  useEffect(() => {
    loadConversations();
    loadActiveGoalsCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh active goals cache when the user returns to the chat tab/window.
  // Throttled so visibilitychange + focus firing back-to-back is one fetch.
  // Silent — chat behavior, streaming, and slash commands are untouched.
  useEffect(() => {
    let lastRefresh = 0;
    const MIN_INTERVAL_MS = 5000;

    const maybeRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefresh < MIN_INTERVAL_MS) return;
      lastRefresh = now;
      loadActiveGoalsCache();
    };

    document.addEventListener('visibilitychange', maybeRefresh);
    window.addEventListener('focus', maybeRefresh);
    return () => {
      document.removeEventListener('visibilitychange', maybeRefresh);
      window.removeEventListener('focus', maybeRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeId) loadMessages(activeId);
    else setMessages([]);
    setSummary(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, thinking]);

  useEffect(() => {
    // autosize textarea
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  /* ---------------- API ---------------- */

  const loadConversations = async () => {
    try {
      const { data } = await axios.get(`${API}/conversations`, { headers: getAuthHeaders() });
      setConversations(data.conversations || []);
    } catch (e) {
      // silent
    }
  };

  // Refresh the in-memory active goals cache used for ambient goal awareness.
  // Lightweight: one network call, stored on a ref so handleSend stays stable.
  const loadActiveGoalsCache = async () => {
    try {
      const { data } = await axios.get(`${API}/goals?status=active`, {
        headers: getAuthHeaders(),
        timeout: 8000,
      });
      activeGoalsRef.current = Array.isArray(data?.goals) ? data.goals : [];
    } catch {
      // Silent — goal awareness is optional; chat must always work.
      activeGoalsRef.current = activeGoalsRef.current || [];
    }
  };

  const loadMessages = async (cid) => {
    try {
      const { data } = await axios.get(`${API}/conversations/${cid}/messages`, {
        headers: getAuthHeaders(),
      });
      setMessages(
        (data.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
        }))
      );
    } catch (e) {
      setMessages([]);
    }
  };

  const deleteConversation = async (cid, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await axios.delete(`${API}/conversations/${cid}`, { headers: getAuthHeaders() });
      if (activeId === cid) {
        setActiveId(null);
        setMessages([]);
      }
      loadConversations();
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  /* ---------------- Streaming send ---------------- */

  const handleSend = useCallback(
    async (overrideText) => {
      const text = (overrideText ?? input).trim();
      const hasImages = attachments.some((a) => a.isImage);
      if ((!text && !hasImages) || loading) return;

      // Intercept /progress before any LLM call. Two cases:
      //   1. "/progress" alone (or with whitespace) → open picker
      //   2. "/progress <n>: <update>" → apply update directly
      // Malformed "/progress …" falls through to normal chat (no breakage).
      if (/^\/progress\b/i.test(text)) {
        setInput('');
        setAttachments([]);
        if (/^\/progress\s*$/i.test(text)) {
          showProgressPicker();
          return;
        }
        const parsed = parseProgressCommand(text);
        if (parsed) {
          await applyProgressUpdate(parsed);
          return;
        }
        // unrecognized format — show a brief inline hint, don't send to LLM
        setMessages((prev) => [
          ...prev,
          { role: 'user', content: text },
          {
            role: 'assistant',
            content:
              "I couldn't parse that. Try `/progress <number>: <update>` — for example: `/progress 1: drafted the welcome email`. Use `/progress` alone to see the list.",
          },
        ]);
        return;
      }

      // Build message content:
      //   - images become [image:<file_id>|<name>] tokens so they persist and re-render
      //   - non-image files listed as [Attached files: ...]
      const images = attachments.filter((a) => a.isImage);
      const otherFiles = attachments.filter((a) => !a.isImage);

      const imageTokens = images.map((a) => `[image:${a.fileId}|${a.name}]`).join('\n');
      const filesNote =
        otherFiles.length > 0
          ? `\n\n[Attached files: ${otherFiles.map((a) => a.name).join(', ')}]`
          : '';

      const composedDisplay = [imageTokens, text].filter(Boolean).join('\n') + filesNote;

      // Command intelligence: if user's text starts with a known command starter,
      // wrap the PAYLOAD with a specialized prompt for LEE. The bubble still
      // shows what the user actually typed (no leakage of the engineered prompt).
      const cmd = detectCommand(text);
      const baseSend = cmd ? COMMAND_PROMPTS[cmd.key](cmd.payload) : composedDisplay;
      let toSend = baseSend || '(image attached)';

      // File intelligence: when non-image files are attached, inject their
      // extracted text + a usage instruction. LEE then summarizes / extracts /
      // answers from the actual content instead of just acknowledging the file.
      // Skipped for slash commands that own their own prompt (build/analyze/etc),
      // images (no text to inject), and files with empty text.
      if (!cmd) {
        const textFiles = otherFiles.filter((a) => a && a.text && a.text.trim());
        if (textFiles.length > 0) {
          toSend = buildFileContextBlock(textFiles) + toSend;
        }
      }

      // Ambient goal awareness: for normal chat (not a structured command),
      // prepend a short [LEE memory: ...] block when active goals look
      // relevant to the message. Empty/irrelevant cases skip this entirely
      // so unrelated chat is unaffected. The user-bubble (composedDisplay)
      // is unchanged — only what's sent to LEE includes the context.
      if (!cmd && text) {
        const relevant = pickRelevantGoals(text, activeGoalsRef.current);
        if (relevant.length > 0) {
          toSend = buildGoalContextBlock(relevant) + toSend;
        }
      }

      // Seed local image URL cache so the bubble renders instantly
      setImageUrls((prev) => {
        const next = { ...prev };
        images.forEach((a) => {
          if (a.previewUrl) next[a.fileId] = a.previewUrl;
        });
        return next;
      });

      setInput('');
      setAttachments([]);
      setMessages((prev) => [...prev, { role: 'user', content: composedDisplay }]);
      setLoading(true);
      setThinking(true);
      setStreamingContent('');

      // V3: a new turn always cancels any in-flight assistant speech.
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.src = '';
        } catch {
          /* noop */
        }
        audioRef.current = null;
      }
      setIsSpeaking(false);

      let finalConvId = activeId;

      try {
        const resp = await fetch(`${API}/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            message: toSend,
            conversation_id: activeId || undefined,
          }),
        });

        if (!resp.ok || !resp.body) throw new Error('Stream failed');

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        let acc = '';

        // read loop
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });

          const lines = buf.split('\n');
          buf = lines.pop() || '';

          for (const raw of lines) {
            if (!raw.startsWith('data:')) continue;
            const jsonStr = raw.slice(5).trim();
            if (!jsonStr) continue;
            try {
              const evt = JSON.parse(jsonStr);
              if (evt.type === 'thinking') {
                setThinking(true);
              } else if (evt.type === 'content') {
                if (thinking) setThinking(false);
                acc += evt.content || '';
                setThinking(false);
                setStreamingContent(acc);
                // Sentence-chunked TTS: ship complete sentences as they arrive
                // so LEE starts speaking ~1s in, not after the whole reply.
                if (speakResponses) {
                  // Reset stream-done flag at first content chunk of a new turn.
                  if (ttsConsumedRef.current === 0 && acc.length > 0) {
                    ttsAbortedRef.current = false;
                    ttsStreamDoneRef.current = false;
                  }
                  ttsFeedStreaming(acc);
                }
              } else if (evt.type === 'done') {
                finalConvId = evt.conversation_id || finalConvId;
                setMessages((prev) => [...prev, { role: 'assistant', content: acc }]);
                setStreamingContent('');
                setThinking(false);
                // Flush any sentence tail without a period.
                if (speakResponses && acc && acc.trim()) {
                  ttsFlushOnDone(acc);
                }
              } else if (evt.type === 'error') {
                toast.error('LEE ran into an issue');
                setMessages((prev) => [
                  ...prev,
                  { role: 'error', content: evt.content || 'Something went wrong.' },
                ]);
              }
            } catch {
              // ignore bad frame
            }
          }
        }
      } catch (err) {
        toast.error('Connection issue');
        setMessages((prev) => [
          ...prev,
          { role: 'error', content: 'Connection issue. Please try again.' },
        ]);
      } finally {
        setLoading(false);
        setThinking(false);
        if (finalConvId && finalConvId !== activeId) {
          setActiveId(finalConvId);
        }
        // refresh sidebar after a beat
        setTimeout(loadConversations, 300);
        inputRef.current?.focus();
      }
    },
    [input, loading, activeId, attachments, thinking, speakResponses, playAssistantSpeech, ttsFeedStreaming, ttsFlushOnDone]
  );

  /* ---------------- Attachments ---------------- */

  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      let imgCount = 0;
      for (const f of files) {
        const isImage = IMAGE_MIME.has(f.type);
        const form = new FormData();
        form.append('file', f);
        const { data } = await axios.post(`${API}/files/upload`, form, {
          headers: { ...getAuthHeaders() },
        });
        const fileId = data?.file_id || data?.id;
        let previewUrl = null;
        if (isImage) {
          previewUrl = URL.createObjectURL(f);
          imgCount += 1;
          if (fileId) {
            setImageUrls((prev) => ({ ...prev, [fileId]: previewUrl }));
          }
        }
        setAttachments((prev) => [
          ...prev,
          {
            name: f.name,
            size: f.size,
            fileId,
            mime: f.type,
            isImage,
            previewUrl,
            // Capture extracted text + type metadata for document intelligence.
            // Images won't have text; that's expected.
            text: typeof data?.text === 'string' ? data.text : '',
            docType: data?.type || '',
            pages: data?.pages,
            rowCount: data?.row_count,
            charCount: data?.char_count,
          },
        ]);
      }
      if (imgCount > 0) {
        toast.success(`${imgCount} image${imgCount > 1 ? 's' : ''} attached`);
      } else {
        toast.success(`${files.length} file${files.length > 1 ? 's' : ''} attached`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => {
      const gone = prev[idx];
      if (gone?.previewUrl) {
        try { URL.revokeObjectURL(gone.previewUrl); } catch {}
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  /* ---------------- Voice input ---------------- */

  const toggleRecording = async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Pick best-supported mimeType. iOS Safari does NOT support webm,
      // so we fall back to mp4/aac which iOS records natively.
      const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
        'audio/aac',
        'audio/ogg;codecs=opus',
      ];
      let chosenMime = '';
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
        chosenMime = candidates.find((m) => MediaRecorder.isTypeSupported(m)) || '';
      }
      const mr = chosenMime ? new MediaRecorder(stream, { mimeType: chosenMime }) : new MediaRecorder(stream);

      // Pick extension matching the actual recorded format. Whisper uses
      // file extension to detect the codec, so this MUST match.
      const recordedMime = mr.mimeType || chosenMime || 'audio/webm';
      const ext = recordedMime.includes('mp4') ? 'mp4'
                : recordedMime.includes('aac') ? 'm4a'
                : recordedMime.includes('ogg') ? 'ogg'
                : 'webm';
      const blobType = recordedMime.split(';')[0]; // strip codecs for Blob type

      audioChunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && audioChunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: blobType });
        if (blob.size < 1000) return;
        const form = new FormData();
        form.append('file', blob, `voice.${ext}`);
        try {
          toast.loading('Transcribing...', { id: 'vtx' });
          const { data } = await axios.post(`${API}/voice/transcribe`, form, {
            headers: { ...getAuthHeaders() },
          });
          toast.dismiss('vtx');
          const text = data?.text || data?.transcript || '';
          if (text) {
            setInput((prev) => (prev ? prev + ' ' : '') + text);
            inputRef.current?.focus();
          } else {
            toast.error('No speech detected');
          }
        } catch (err) {
          toast.dismiss('vtx');
          const msg = err?.response?.data?.detail || 'Transcription failed';
          toast.error(typeof msg === 'string' ? msg : 'Transcription failed');
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (err) {
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        toast.error('Microphone permission denied');
      } else if (name === 'NotFoundError') {
        toast.error('No microphone found');
      } else {
        toast.error('Could not start recording on this device');
      }
    }
  };

  /* ---------------- Voice Mode (continuous, hands-free) ---------------- */
  // Click once -> mic stays hot. Browser SpeechRecognition produces final
  // transcripts; each final is auto-sent through handleSend(). LEE's reply
  // streams + speaks via the existing playAssistantSpeech() (gpt-5.2 + tts-1
  // onyx). When LEE finishes speaking, recognition auto-restarts.
  //
  // Echo prevention strategy (hard mute):
  //  - The instant LEE begins generating (loading) OR speaking (isSpeaking),
  //    we abort the recognition object AND release the underlying mic stream
  //    so the browser physically stops capturing audio. The OS-level "mic
  //    active" indicator goes off.
  //  - A vmGateRef prevents any in-flight onresult fired during teardown
  //    from being processed.
  //  - Recognition is only restarted after BOTH loading and isSpeaking are
  //    false, plus a 700ms cooldown for audio output to drain.
  //
  // No new backend. No new pages. ONE LEE preserved.
  const [voiceMode, setVoiceMode] = useState(false);
  const [vmListening, setVmListening] = useState(false);
  const [vmInterim, setVmInterim] = useState('');
  const recognitionRef = useRef(null);
  const voiceModeRef = useRef(false);
  const loadingRef = useRef(false);
  const isSpeakingRef = useRef(false);
  const vmGateRef = useRef(false); // true => ignore any recognition output

  // iOS / no-SpeechRecognition fallback: continuous MediaRecorder + silence detection
  const vmRecorderRef = useRef(null);   // MediaRecorder instance
  const vmStreamRef = useRef(null);     // active MediaStream
  const vmAudioCtxRef = useRef(null);   // AudioContext for silence analysis
  const vmAnalyserRef = useRef(null);   // AnalyserNode
  const vmRafRef = useRef(null);        // requestAnimationFrame id
  const vmChunksRef = useRef([]);
  const vmSilenceStartRef = useRef(0);
  const vmSpokeRef = useRef(false);     // user actually spoke this turn
  const vmRecMimeRef = useRef('audio/webm');
  const vmRecExtRef = useRef('webm');

  // Detect iOS or browsers without reliable SpeechRecognition. iOS Safari
  // technically has webkitSpeechRecognition but it's unreliable and often
  // captures nothing — fall back to MediaRecorder + Whisper instead.
  const _isIOSDevice = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isiOS = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return isiOS && !window.MSStream;
  };
  const _hasReliableSR = () => {
    if (_isIOSDevice()) return false;
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  };

  useEffect(() => {
    voiceModeRef.current = voiceMode;
  }, [voiceMode]);
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);

  const _vmHardStop = useCallback(() => {
    vmGateRef.current = true; // drop anything still in flight
    const rec = recognitionRef.current;
    if (rec) {
      // abort() is immediate and discards pending results; stop() is async.
      try {
        if (typeof rec.abort === 'function') rec.abort();
        else rec.stop();
      } catch {
        /* noop */
      }
    }
    recognitionRef.current = null;
    // Tear down MediaRecorder fallback (iOS path) if active.
    if (vmRafRef.current) {
      try { cancelAnimationFrame(vmRafRef.current); } catch { /* noop */ }
      vmRafRef.current = null;
    }
    if (vmRecorderRef.current) {
      try { vmRecorderRef.current.ondataavailable = null; } catch { /* noop */ }
      try { vmRecorderRef.current.onstop = null; } catch { /* noop */ }
      try {
        if (vmRecorderRef.current.state !== 'inactive') vmRecorderRef.current.stop();
      } catch { /* noop */ }
      vmRecorderRef.current = null;
    }
    if (vmStreamRef.current) {
      try {
        vmStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch { /* noop */ }
      vmStreamRef.current = null;
    }
    if (vmAudioCtxRef.current) {
      try { vmAudioCtxRef.current.close(); } catch { /* noop */ }
      vmAudioCtxRef.current = null;
    }
    vmAnalyserRef.current = null;
    vmChunksRef.current = [];
    vmSpokeRef.current = false;
    vmSilenceStartRef.current = 0;
    setVmListening(false);
    setVmInterim('');
  }, []);

  // iOS / fallback path: continuous MediaRecorder + silence detection. When
  // ~1.5s of silence is detected AFTER the user has spoken, we stop, send to
  // Whisper, and forward the transcript to handleSend(). The existing
  // loading/isSpeaking gate effect tears us down via _vmHardStop().
  const _vmStartMediaRecorder = useCallback(async () => {
    if (!voiceModeRef.current) return;
    if (loadingRef.current || isSpeakingRef.current) return;
    if (vmRecorderRef.current) return;

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error('Microphone permission denied');
      setVoiceMode(false);
      return;
    }

    // Pick best supported mimeType (iOS = mp4, Chrome = webm/opus).
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4',
      'audio/aac',
      'audio/ogg;codecs=opus',
    ];
    let chosen = '';
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
      chosen = candidates.find((m) => MediaRecorder.isTypeSupported(m)) || '';
    }
    let mr;
    try {
      mr = chosen ? new MediaRecorder(stream, { mimeType: chosen }) : new MediaRecorder(stream);
    } catch {
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      toast.error('Recording not supported on this device');
      setVoiceMode(false);
      return;
    }

    const recordedMime = mr.mimeType || chosen || 'audio/webm';
    const ext = recordedMime.includes('mp4') ? 'mp4'
              : recordedMime.includes('aac') ? 'm4a'
              : recordedMime.includes('ogg') ? 'ogg'
              : 'webm';
    vmRecMimeRef.current = recordedMime.split(';')[0];
    vmRecExtRef.current = ext;
    vmChunksRef.current = [];
    vmSpokeRef.current = false;
    vmSilenceStartRef.current = 0;

    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) vmChunksRef.current.push(e.data);
    };
    mr.onstop = async () => {
      // Always clean up the stream + audio graph; another start cycle will
      // re-acquire them after LEE finishes responding.
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      if (vmAudioCtxRef.current) {
        try { vmAudioCtxRef.current.close(); } catch { /* noop */ }
        vmAudioCtxRef.current = null;
      }
      vmAnalyserRef.current = null;
      vmStreamRef.current = null;
      vmRecorderRef.current = null;
      setVmListening(false);

      // If we tore down because the user actually spoke, transcribe.
      // Otherwise (gate fired, no speech), discard.
      const spoke = vmSpokeRef.current;
      const chunks = vmChunksRef.current;
      vmChunksRef.current = [];
      vmSpokeRef.current = false;
      if (!spoke || vmGateRef.current) return;

      const blob = new Blob(chunks, { type: vmRecMimeRef.current });
      if (blob.size < 1500) return; // too small to be real speech
      const form = new FormData();
      form.append('file', blob, `vm.${vmRecExtRef.current}`);
      try {
        const { data } = await axios.post(`${API}/voice/transcribe`, form, {
          headers: { ...getAuthHeaders() },
        });
        const text = (data?.text || data?.transcript || '').trim();
        if (!text) return;
        // Mirror the SR path: subtle ack, then send.
        _playAcousticAck();
        _playVoiceAck();
        handleSend(text);
      } catch {
        /* swallow — continuous loop will retry next utterance */
      }
    };

    // Build silence detector
    let AC;
    try {
      AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      // iOS Safari starts AudioContext in 'suspended' state until a user
      // gesture explicitly resumes it. Without this, the analyser returns
      // pure silence forever and we never detect speech.
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch { /* noop */ }
      }
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      vmAudioCtxRef.current = ctx;
      vmAnalyserRef.current = analyser;
    } catch {
      // Without an AudioContext we have no silence detection; bail gracefully.
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      toast.error('Voice mode not supported on this browser');
      setVoiceMode(false);
      return;
    }

    vmRecorderRef.current = mr;
    vmStreamRef.current = stream;
    vmGateRef.current = false;
    try {
      // Some iOS Safari versions reject the timeslice argument. Try with,
      // then without — either way the data is collected on stop().
      try { mr.start(250); } catch { mr.start(); }
    } catch {
      _vmHardStop();
      return;
    }
    setVmListening(true);

    // Thresholds — tuned for mobile mic gain (iOS tends to attenuate input).
    const SPEECH_RMS = 0.012;           // RMS above this counts as speech
    const SILENCE_AFTER_SPEECH_MS = 1300; // stop after this much trailing silence
    const MAX_UTTERANCE_MS = 15000;     // hard cap per utterance
    const MAX_WAIT_FOR_SPEECH_MS = 12000; // if user never speaks, restart loop
    const startedAt = Date.now();

    const buf = new Uint8Array(vmAnalyserRef.current.fftSize);
    const tick = () => {
      const analyser = vmAnalyserRef.current;
      const rec = vmRecorderRef.current;
      if (!analyser || !rec || rec.state === 'inactive') return;
      if (vmGateRef.current || !voiceModeRef.current) return;
      if (loadingRef.current || isSpeakingRef.current) return;

      analyser.getByteTimeDomainData(buf);
      // RMS in [0,1] (samples are unsigned 0..255 centered at 128)
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);

      const now = Date.now();
      if (rms > SPEECH_RMS) {
        vmSpokeRef.current = true;
        vmSilenceStartRef.current = 0;
      } else if (vmSpokeRef.current) {
        if (vmSilenceStartRef.current === 0) vmSilenceStartRef.current = now;
        if (now - vmSilenceStartRef.current >= SILENCE_AFTER_SPEECH_MS) {
          // End of utterance — stop the recorder, onstop handles transcription.
          try { rec.stop(); } catch { /* noop */ }
          return;
        }
      }

      // Safety cap: even if no silence detected, end the utterance.
      if (now - startedAt >= MAX_UTTERANCE_MS && vmSpokeRef.current) {
        try { rec.stop(); } catch { /* noop */ }
        return;
      }

      // If we've been listening for a long time and the user never spoke,
      // recycle the stream — sometimes iOS releases the mic permission and
      // we need a fresh capture cycle to start hearing again.
      if (!vmSpokeRef.current && now - startedAt >= MAX_WAIT_FOR_SPEECH_MS) {
        try { rec.stop(); } catch { /* noop */ }
        // Schedule a restart on the next tick.
        setTimeout(() => {
          if (voiceModeRef.current && !loadingRef.current && !isSpeakingRef.current) {
            _vmStartMediaRecorder();
          }
        }, 200);
        return;
      }

      vmRafRef.current = requestAnimationFrame(tick);
    };
    vmRafRef.current = requestAnimationFrame(tick);
  }, [handleSend, _playAcousticAck, _playVoiceAck, _vmHardStop]);

  const _vmStart = useCallback(() => {
    if (!voiceModeRef.current) return;
    if (loadingRef.current || isSpeakingRef.current) return;
    if (recognitionRef.current || vmRecorderRef.current) return; // already running

    // iOS Safari / no SpeechRecognition -> MediaRecorder fallback
    if (!_hasReliableSR()) {
      _vmStartMediaRecorder();
      return;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      _vmStartMediaRecorder();
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onstart = () => {
      vmGateRef.current = false;
      setVmListening(true);
    };

    rec.onresult = (event) => {
      // Hard gate: while LEE is generating/speaking (or we just tore down),
      // discard anything the mic captured. This kills the echo path even if
      // an onresult races the abort().
      if (
        vmGateRef.current ||
        loadingRef.current ||
        isSpeakingRef.current ||
        !voiceModeRef.current
      ) {
        return;
      }

      let interim = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        const t = r[0]?.transcript || '';
        if (r.isFinal) finalText += t;
        else interim += t;
      }
      setVmInterim(interim);
      const trimmed = finalText.trim();
      if (!trimmed) return;

      // Drop suspiciously short snippets that are likely room/echo noise.
      const words = trimmed.split(/\s+/).filter(Boolean);
      if (words.length < 2 && trimmed.length < 6) return;

      setVmInterim('');
      // Lock the gate immediately — anything else captured before teardown
      // completes will be ignored.
      vmGateRef.current = true;
      // Tear down the mic synchronously so the browser stops capturing.
      try {
        if (typeof rec.abort === 'function') rec.abort();
        else rec.stop();
      } catch {
        /* noop */
      }
      recognitionRef.current = null;
      setVmListening(false);
      // P2.2: immediate "I heard you" feedback — acoustic micro-ack (subtle
      // tick) + optional voice ack ("Mmhm" / "Got it"). Fires BEFORE the LLM
      // round-trip so the user never wonders whether LEE caught it.
      _playAcousticAck();
      _playVoiceAck();
      handleSend(trimmed);
    };

    rec.onerror = (e) => {
      if (e?.error && !['no-speech', 'aborted'].includes(e.error)) {
        // eslint-disable-next-line no-console
        console.warn('voice-mode recognition error:', e.error);
      }
    };

    rec.onend = () => {
      setVmListening(false);
      // NEVER restart from inside onend — the gating useEffect owns timing.
      if (recognitionRef.current === rec) recognitionRef.current = null;
    };

    try {
      vmGateRef.current = false;
      rec.start();
      recognitionRef.current = rec;
    } catch {
      /* start may throw if already running; harmless */
    }
  }, [handleSend, _playAcousticAck, _playVoiceAck]);

  const toggleVoiceMode = useCallback(async () => {
    if (voiceMode) {
      setVoiceMode(false);
      _vmHardStop();
      // Stop any in-flight TTS so the user gets immediate silence.
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.src = '';
        } catch {
          /* noop */
        }
        audioRef.current = null;
        setIsSpeaking(false);
      }
      toast('Voice mode off', { icon: '🔇' });
      return;
    }
    // Going ON.
    if (!_hasReliableSR()) {
      // iOS Safari / browsers without reliable SpeechRecognition use the
      // MediaRecorder fallback — no need to block them here. We do need
      // MediaRecorder + getUserMedia, which all modern mobile browsers have.
      if (typeof MediaRecorder === 'undefined') {
        toast.error('Voice Mode not supported on this browser');
        return;
      }
    } else {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) {
        toast.error('Voice Mode needs Chrome or Edge (no Speech API in this browser)');
        return;
      }
    }
    // Pre-flight mic permission probe. CRITICAL: must release the stream
    // immediately, otherwise iOS will refuse the next getUserMedia call
    // inside _vmStartMediaRecorder (only one capture at a time on iOS).
    try {
      const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
      try { probe.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    } catch {
      toast.error('Microphone permission required for Voice Mode');
      return;
    }
    if (!speakResponses) {
      setSpeakResponses(true);
      try {
        localStorage.setItem('lee_speak_responses', '1');
      } catch {
        /* noop */
      }
    }
    setVoiceMode(true);
    toast.success('Voice mode on — talk naturally');
    // Pre-warm backend TTS connection + AudioContext so the very first
    // sentence has minimal handshake cost.
    _ttsWarmup();
    setTimeout(() => _vmStart(), 80);
  }, [voiceMode, _vmHardStop, _vmStart, speakResponses, setIsSpeaking, _ttsWarmup]);

  // Gate: when LEE starts thinking or speaking, KILL the mic immediately.
  // When both are clear, restart after a cooldown so audio output drains.
  useEffect(() => {
    if (!voiceMode) return undefined;
    if (loading || isSpeaking) {
      // Kill mic now.
      _vmHardStop();
      return undefined;
    }
    // Both clear — schedule restart unless we got pre-empted.
    const t = setTimeout(() => {
      if (
        voiceModeRef.current &&
        !loadingRef.current &&
        !isSpeakingRef.current &&
        !recognitionRef.current
      ) {
        _vmStart();
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMode, loading, isSpeaking]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      voiceModeRef.current = false;
      _vmHardStop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- Slash command palette ---------------- */

  // Strip image tokens for sending to summary LLM
  const stripImageTokens = (s) => (s || '').replace(IMAGE_TOKEN, '[image]').trim();

  const generateSummary = async () => {
    if (summaryLoading) return;
    const all = messages.filter((m) => m.role === 'user' || m.role === 'assistant');
    if (all.length === 0) {
      toast.error('Nothing to summarize yet');
      return;
    }
    // Build compact transcript from last 20 messages
    const slice = all.slice(-20);
    const transcript = slice
      .map((m) => `${m.role === 'user' ? 'You' : 'LEE'}: ${stripImageTokens(m.content).slice(0, 600)}`)
      .join('\n');

    const prompt =
      'Summarize the following conversation in ONE short sentence of no more than 14 words. No preamble, no quotes, no markdown, no emojis. Just the sentence.\n\n' +
      transcript;

    setSummaryLoading(true);
    setSummary({ text: '', generating: true });

    try {
      const { data } = await axios.post(
        `${API}/chat`,
        { message: prompt, conversation_id: null },
        { headers: getAuthHeaders(), timeout: 45000 }
      );
      const text = (data?.response || '').trim().replace(/^["']|["']$/g, '');
      setSummary({ text: text || 'Nothing notable yet.', generating: false });
    } catch {
      setSummary(null);
      toast.error('Summary failed');
    } finally {
      setSummaryLoading(false);
    }
  };

  // Filter commands by query (chars after the leading /)
  const filteredCommands = COMMANDS.filter((c) =>
    c.key.startsWith(paletteQuery.toLowerCase())
  );

  /* ---------------- Goal slash commands ---------------- */

  const formatGoalsList = (goals) => {
    if (!goals || goals.length === 0) {
      return "You don't have any active goals saved yet. Open `/goals` page to add one, or tell me what you want to start tracking.";
    }
    const lines = goals.map((g) => {
      const prio = (g.priority || 'medium').toUpperCase();
      const due = g.deadline ? ` · due ${g.deadline}` : '';
      const next = g.next_step ? `\n  next: ${g.next_step}` : '';
      return `**[${prio}] ${g.title}**${due}${next}`;
    });
    return `Here's what you're tracking right now (${goals.length} active):\n\n` + lines.join('\n\n');
  };

  const showGoals = async () => {
    setMessages((prev) => [...prev, { role: 'user', content: '/goals' }]);
    try {
      const { data } = await axios.get(`${API}/goals?status=active`, {
        headers: getAuthHeaders(),
        timeout: 15000,
      });
      const goalsList = data?.goals || [];
      activeGoalsRef.current = goalsList; // refresh ambient cache
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: formatGoalsList(goalsList) },
      ]);
    } catch {
      toast.error('Could not load goals');
      setMessages((prev) => [
        ...prev,
        { role: 'error', content: "I couldn't load your goals just now. Please try again." },
      ]);
    }
  };

  const showFocus = async () => {
    setMessages((prev) => [...prev, { role: 'user', content: '/focus' }]);
    setThinking(true);
    try {
      const { data } = await axios.get(`${API}/goals/focus`, {
        headers: getAuthHeaders(),
        timeout: 45000,
      });
      const summary = (data?.summary || '').trim();
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: summary || "You don't have any active goals to focus on yet." },
      ]);
    } catch {
      toast.error('Focus brief failed');
      setMessages((prev) => [
        ...prev,
        { role: 'error', content: "I couldn't generate a focus brief right now. Please try again." },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const suggestNextAction = async () => {
    setMessages((prev) => [...prev, { role: 'user', content: '/next' }]);
    setThinking(true);
    try {
      // Pick the single highest-priority active goal, then ask for its next step.
      const list = await axios.get(`${API}/goals?status=active`, {
        headers: getAuthHeaders(),
        timeout: 15000,
      });
      const goals = list?.data?.goals || [];
      if (goals.length === 0) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: "You don't have any active goals saved yet. Add one and I'll suggest the next move.",
          },
        ]);
        return;
      }
      const rank = { critical: 0, high: 1, medium: 2, low: 3 };
      const top = [...goals].sort((a, b) => {
        const r = (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9);
        if (r !== 0) return r;
        return (a.deadline || 'z').localeCompare(b.deadline || 'z');
      })[0];

      const { data } = await axios.post(
        `${API}/goals/${top.id}/suggest-next-step`,
        {},
        { headers: getAuthHeaders(), timeout: 45000 }
      );
      const suggestion = (data?.suggestion || '').trim();
      const content = suggestion
        ? `On **${top.title}** — ${suggestion}`
        : `Pick up **${top.title}** next; it's your highest-priority active goal.`;
      setMessages((prev) => [...prev, { role: 'assistant', content }]);
    } catch {
      toast.error('Next-step suggestion failed');
      setMessages((prev) => [
        ...prev,
        { role: 'error', content: "I couldn't suggest a next step right now. Please try again." },
      ]);
    } finally {
      setThinking(false);
    }
  };

  // Sort active goals the same way /next does
  const sortGoalsByPriority = (goals) => {
    const rank = { critical: 0, high: 1, medium: 2, low: 3 };
    return [...goals].sort((a, b) => {
      const r = (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9);
      if (r !== 0) return r;
      return (a.deadline || 'z').localeCompare(b.deadline || 'z');
    });
  };

  const showProgressPicker = async () => {
    setMessages((prev) => [...prev, { role: 'user', content: '/progress' }]);
    try {
      const { data } = await axios.get(`${API}/goals?status=active`, {
        headers: getAuthHeaders(),
        timeout: 15000,
      });
      const goals = sortGoalsByPriority(data?.goals || []);
      if (goals.length === 0) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: "You don't have any active goals yet. Add one first, then `/progress` will let you log updates.",
          },
        ]);
        return;
      }
      const lines = goals.map((g, i) => {
        const prio = (g.priority || 'medium').toUpperCase();
        const next = g.next_step ? ` — currently: ${g.next_step}` : '';
        return `${i + 1}. **[${prio}] ${g.title}**${next}`;
      });
      const helpMsg =
        "Which goal are you updating? Reply with the number + your progress, like:\n\n" +
        "  `/progress 1: drafted the welcome email`\n\n" +
        "Add **done** / **completed** to close it, or **pause** to pause it. " +
        "Otherwise I'll just update the next step.\n\n" +
        lines.join('\n');
      setMessages((prev) => [...prev, { role: 'assistant', content: helpMsg }]);
      // Pre-fill input so the user can immediately type the number + update
      setInput('/progress ');
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          el.focus();
          const len = '/progress '.length;
          el.setSelectionRange(len, len);
        }
      });
    } catch {
      toast.error('Could not load goals');
      setMessages((prev) => [
        ...prev,
        { role: 'error', content: "I couldn't load your goals just now. Please try again." },
      ]);
    }
  };

  // Parse input like "/progress 1: drafted welcome email"
  // Accepts separators ":", ".", "-" between number and update.
  // Returns { index, update } or null if format isn't recognized.
  const parseProgressCommand = (raw) => {
    if (!raw) return null;
    const m = raw.trim().match(/^\/progress\s+(\d+)\s*[:.\-]?\s*(.+)$/i);
    if (!m) return null;
    const index = parseInt(m[1], 10);
    const update = (m[2] || '').trim();
    if (!Number.isFinite(index) || index < 1 || !update) return null;
    return { index, update };
  };

  // Determine status change from user's text. Conservative: only matches whole words.
  const detectStatusChange = (update) => {
    const t = update.toLowerCase();
    if (/\b(done|completed|complete|finished|shipped)\b/.test(t)) return 'completed';
    if (/\b(pause|paused|on hold|holding)\b/.test(t)) return 'paused';
    return null;
  };

  const applyProgressUpdate = async (parsed) => {
    // User's raw command bubble
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: `/progress ${parsed.index}: ${parsed.update}` },
    ]);

    try {
      // Re-fetch active goals so the numbering reflects current state
      const { data } = await axios.get(`${API}/goals?status=active`, {
        headers: getAuthHeaders(),
        timeout: 15000,
      });
      const goals = sortGoalsByPriority(data?.goals || []);
      if (goals.length === 0) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: "There are no active goals to update right now.",
          },
        ]);
        return;
      }
      if (parsed.index > goals.length) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `I only see ${goals.length} active goal${goals.length === 1 ? '' : 's'}. Try \`/progress\` again to see the current list.`,
          },
        ]);
        return;
      }

      const goal = goals[parsed.index - 1];
      const newStatus = detectStatusChange(parsed.update);

      // Conservative payload: only next_step (always) + status (only if intent is clear).
      // Never touches title/description/priority/category/deadline.
      const payload = { next_step: parsed.update };
      if (newStatus) payload.status = newStatus;

      await axios.patch(`${API}/goals/${goal.id}`, payload, {
        headers: getAuthHeaders(),
        timeout: 15000,
      });

      // Refresh ambient cache so the new next_step / status flows into
      // future normal-chat responses immediately.
      loadActiveGoalsCache();

      let confirmation = `Got it. Updated **${goal.title}**.\n\nNew next step: ${parsed.update}`;
      if (newStatus === 'completed') {
        confirmation += `\n\nMarked as **completed**. Nice work — that one's off your plate.`;
      } else if (newStatus === 'paused') {
        confirmation += `\n\nMarked as **paused**. I'll keep it out of your focus brief until you resume it.`;
      } else {
        confirmation += `\n\nLast activity timestamp refreshed.`;
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: confirmation }]);
    } catch (err) {
      toast.error('Could not save update');
      setMessages((prev) => [
        ...prev,
        { role: 'error', content: "I couldn't save that progress update. Please try again." },
      ]);
    }
  };

  // Recompute palette state from current input value (call on every change)
  const recomputePalette = (value) => {
    if (value.startsWith('/') && !value.includes(' ') && !value.includes('\n')) {
      const q = value.slice(1);
      setPaletteQuery(q);
      setPaletteOpen(true);
      setPaletteIdx(0);
    } else if (paletteOpen) {
      setPaletteOpen(false);
      setPaletteQuery('');
    }
  };

  const onInputChange = (e) => {
    const v = e.target.value;
    setInput(v);
    recomputePalette(v);
  };

  const selectCommand = (cmd) => {
    setPaletteOpen(false);
    setPaletteQuery('');
    if (cmd.key === 'file') {
      setInput('');
      fileInputRef.current?.click();
      return;
    }
    if (cmd.key === 'new') {
      setInput('');
      startNewChat();
      return;
    }
    if (cmd.key === 'summary') {
      setInput('');
      generateSummary();
      return;
    }
    if (cmd.key === 'goals') {
      setInput('');
      showGoals();
      return;
    }
    if (cmd.key === 'focus') {
      setInput('');
      showFocus();
      return;
    }
    if (cmd.key === 'next') {
      setInput('');
      suggestNextAction();
      return;
    }
    if (cmd.key === 'progress') {
      setInput('');
      showProgressPicker();
      return;
    }
    setInput(cmd.starter);
    // place cursor at the end
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const len = cmd.starter.length;
        el.setSelectionRange(len, len);
      }
    });
  };

  /* ---------------- Misc handlers ---------------- */

  const onKeyDown = (e) => {
    // Palette-specific keys first
    if (paletteOpen && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setPaletteIdx((i) => (i + 1) % filteredCommands.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setPaletteIdx((i) => (i - 1 + filteredCommands.length) % filteredCommands.length);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        selectCommand(filteredCommands[paletteIdx]);
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        selectCommand(filteredCommands[paletteIdx]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setPaletteOpen(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const onScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 120);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const startNewChat = () => {
    setActiveId(null);
    setMessages([]);
    setStreamingContent('');
    setInput('');
    setAttachments([]);
    setSummary(null);
    if (window.innerWidth < 768) setSidebarOpen(false);
    inputRef.current?.focus();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  /* ---------------- Derived ---------------- */

  const filtered = conversations.filter((c) =>
    (c.title || c.last_message || '').toLowerCase().includes(search.toLowerCase())
  );
  const buckets = groupByDate(filtered);

  const suggestionChips = [
    { label: 'Build business', prompt: 'Help me build a business from a raw idea. Ask me one sharp question to start.' },
    { label: 'Draft document', prompt: 'Draft a document for me. What type — memo, one-pager, brief, proposal?' },
    { label: 'Analyze idea', prompt: 'I want you to pressure-test an idea. What are the weakest assumptions and hidden costs?' },
    { label: 'Daily plan', prompt: 'Design my next 48 hours for highest-leverage output. Ask me what matters most right now.' },
  ];

  const quickActions = [
    { label: 'Generate', icon: Sparkles, prompt: 'Generate: ' },
    { label: 'Analyze', icon: Zap, prompt: 'Analyze: ' },
    { label: 'Build', icon: Wrench, prompt: 'Build: ' },
  ];

  /* ---------------- Render ---------------- */

  return (
    <Layout fullBleed>
    <div
      data-testid="chat-root"
      className="relative h-[100dvh] w-full bg-[#070707] text-zinc-200 flex overflow-hidden pb-16 md:pb-0"
      style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
    >
      {/* ambient background glow */}
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-amber-500/[0.025] rounded-full blur-[160px]" />

      {/* mobile drawer backdrop */}
      {sidebarOpen && (
        <div
          data-testid="sidebar-backdrop"
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* =================== SIDEBAR =================== */}
      <aside
        data-testid="chat-sidebar"
        className={`z-40 border-r border-white/[0.05] bg-[#050505]/95 backdrop-blur-xl flex flex-col overflow-hidden
          fixed inset-y-0 left-0 w-[288px] transform transition-transform duration-300 ease-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }
          md:relative md:h-full md:translate-x-0 md:transform-none md:transition-all ${
            sidebarOpen ? 'md:w-[288px]' : 'md:w-0'
          }`}
      >
        <div className="px-4 pt-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
              <span className="text-[13px] font-bold font-mono text-amber-400">L</span>
            </div>
            <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">
              LEE
            </span>
          </div>
          <button
            data-testid="collapse-sidebar-btn"
            onClick={() => setSidebarOpen(false)}
            className="p-1.5 rounded-md text-zinc-600 hover:text-zinc-300 hover:bg-white/[0.04] transition"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
            <input
              data-testid="sidebar-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations"
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg pl-9 pr-3 py-2 text-[13px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/30 focus:bg-white/[0.05] transition"
            />
          </div>
        </div>

        {/* New chat */}
        <div className="px-3 pb-3">
          <button
            data-testid="new-chat-btn"
            onClick={startNewChat}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-amber-500/[0.08] hover:bg-amber-500/[0.14] border border-amber-500/20 hover:border-amber-500/40 text-amber-300 hover:text-amber-200 text-[13px] font-medium transition-all"
          >
            <Plus className="w-4 h-4" />
            New conversation
          </button>
        </div>

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-2 pb-4">
          {Object.entries(buckets).map(([label, items]) =>
            items.length === 0 ? null : (
              <div key={label} className="mb-4">
                <div className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-600">
                  {label}
                </div>
                <div className="space-y-0.5">
                  {items.map((c) => (
                    <button
                      key={c.conversation_id}
                      data-testid={`conversation-item-${c.conversation_id}`}
                      onClick={() => {
                        setActiveId(c.conversation_id);
                        if (window.innerWidth < 768) setSidebarOpen(false);
                      }}
                      className={`w-full group flex items-center justify-between gap-2 px-2.5 py-2 rounded-md text-left transition-colors ${
                        activeId === c.conversation_id
                          ? 'bg-white/[0.06] text-zinc-100'
                          : 'text-zinc-400 hover:bg-white/[0.03] hover:text-zinc-200'
                      }`}
                    >
                      <span className="text-[13px] truncate flex-1">
                        {(c.title || c.last_message || 'New conversation').replace(IMAGE_TOKEN, 'Photo: $2')}
                      </span>
                      <span
                        onClick={(e) => deleteConversation(c.conversation_id, e)}
                        className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition"
                        data-testid={`delete-conv-${c.conversation_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )
          )}

          {conversations.length === 0 && (
            <div className="px-3 py-6 text-center">
              <p className="text-[12px] text-zinc-600 leading-relaxed">
                Your conversations will live here.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/[0.05] px-3 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 border border-white/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[11px] font-semibold text-zinc-300">
                {(user?.full_name || user?.email || 'U')[0].toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <div className="text-[12px] text-zinc-300 truncate">
                {user?.full_name || 'Owner'}
              </div>
              <div className="text-[10px] text-zinc-600 truncate font-mono">
                {user?.email}
              </div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="p-1.5 rounded-md text-zinc-600 hover:text-zinc-300 hover:bg-white/[0.04] transition"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* =================== MAIN =================== */}
      <main className="relative z-10 flex-1 flex flex-col h-full min-w-0">
        {/* Top bar */}
        <header className="h-12 flex items-center justify-between px-4 border-b border-white/[0.04] bg-[#070707]/60 backdrop-blur">
          <div className="flex items-center gap-2">
            {!sidebarOpen && (
              <button
                data-testid="open-sidebar-btn"
                onClick={() => setSidebarOpen(true)}
                className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.04] transition"
                title="Open sidebar"
              >
                <PanelLeft className="w-4 h-4" />
              </button>
            )}
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-600">
              {activeId ? 'Conversation' : 'New chat'}
            </span>
          </div>
          <button
            data-testid="top-new-chat"
            onClick={startNewChat}
            className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.04] transition"
            title="New conversation"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </header>

        {/* Messages */}
        <div
          ref={scrollContainerRef}
          onScroll={onScroll}
          className="flex-1 overflow-y-auto scrollbar-hide"
          data-testid="messages-scroll"
          style={{ scrollBehavior: 'smooth' }}
        >
          <div className="max-w-[760px] mx-auto px-3 py-3 sm:px-6 sm:py-10">
            {/* Summary banner — elegant inline */}
            {summary && (
              <div
                data-testid="summary-banner"
                className="mb-8 rounded-xl border border-amber-500/20 bg-gradient-to-r from-amber-500/[0.05] via-amber-500/[0.03] to-transparent px-4 py-3 flex items-start gap-3 animate-[slashPaletteIn_180ms_cubic-bezier(0.2,0.8,0.2,1)]"
              >
                <div className="w-6 h-6 rounded-md bg-amber-500/15 border border-amber-500/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkle className="w-3 h-3 text-amber-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-400/70 mb-0.5">
                    Thread summary
                  </div>
                  <div
                    data-testid="summary-text"
                    className="text-[14px] text-zinc-100 leading-relaxed"
                  >
                    {summary.generating ? (
                      <span className="inline-flex items-center gap-2 text-zinc-400">
                        <span className="flex gap-1">
                          <span className="w-1 h-1 rounded-full bg-amber-400/70 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1 h-1 rounded-full bg-amber-400/50 animate-bounce" style={{ animationDelay: '140ms' }} />
                          <span className="w-1 h-1 rounded-full bg-amber-400/30 animate-bounce" style={{ animationDelay: '280ms' }} />
                        </span>
                        <span className="text-[12px] font-mono tracking-wide">Distilling…</span>
                      </span>
                    ) : (
                      summary.text
                    )}
                  </div>
                </div>
                <button
                  data-testid="summary-dismiss"
                  onClick={() => setSummary(null)}
                  className="text-zinc-600 hover:text-zinc-300 transition p-0.5"
                  title="Dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <AlertsBanner />

            {messages.length === 0 && !streamingContent && !thinking ? (
              <EmptyState onPick={(t) => handleSend(t)} />
            ) : (
              <div className="space-y-8">
                {messages.map((m, idx) => (
                  <MessageBubble
                    key={idx}
                    role={m.role}
                    content={m.content}
                    imageUrls={imageUrls}
                    setImageUrls={setImageUrls}
                    onReplay={playAssistantSpeech}
                    isSpeaking={isSpeaking}
                    stopSpeaking={stopSpeaking}
                  />
                ))}

                {thinking && <ThinkingIndicator />}

                {streamingContent && (
                  <MessageBubble role="assistant" content={streamingContent} streaming imageUrls={imageUrls} setImageUrls={setImageUrls} />
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Scroll-to-bottom */}
        {showScrollBtn && (
          <div className="relative z-20 flex justify-center -mt-10 mb-1">
            <button
              data-testid="scroll-bottom-btn"
              onClick={scrollToBottom}
              className="p-2 rounded-full bg-zinc-900/90 backdrop-blur border border-white/10 text-zinc-400 hover:text-white shadow-xl transition"
            >
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* =================== INPUT =================== */}
        <div className="pb-3 sm:pb-6 pt-2 px-3 sm:px-6">
          <div className="max-w-[760px] mx-auto">
            {/* Suggestion chips (only when no messages) */}
            {messages.length === 0 && !streamingContent && (
              <div
                data-testid="suggestion-chips"
                className="flex gap-2 mb-2 overflow-x-auto scrollbar-hide sm:flex-wrap sm:justify-center sm:mb-3"
              >
                {suggestionChips.map((s) => (
                  <button
                    key={s.label}
                    data-testid={`suggest-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => handleSend(s.prompt)}
                    className="shrink-0 whitespace-nowrap px-3.5 py-1.5 text-[12px] text-zinc-400 hover:text-amber-300 bg-white/[0.03] hover:bg-amber-500/[0.06] border border-white/[0.06] hover:border-amber-500/30 rounded-full transition-all"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="attachment-list">
                {attachments.map((a, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-2.5 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[11px] text-zinc-300"
                  >
                    <Paperclip className="w-3 h-3 text-amber-400" />
                    <span className="max-w-[140px] truncate">{a.name}</span>
                    <button
                      onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))}
                      className="text-zinc-500 hover:text-red-400"
                      data-testid={`remove-attachment-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Input bar */}
            <div
              data-testid="chat-input-bar"
              className="relative rounded-2xl bg-white/[0.035] border border-white/[0.08] focus-within:border-amber-500/35 focus-within:bg-white/[0.05] shadow-[0_8px_40px_-12px_rgba(0,0,0,0.6)] transition-all"
            >
              {/* Slash command palette — floats above input */}
              {paletteOpen && filteredCommands.length > 0 && (
                <div
                  data-testid="slash-palette"
                  className="absolute left-0 right-0 bottom-full mb-2 z-30 rounded-xl bg-[#0b0b0b]/95 backdrop-blur-xl border border-white/[0.08] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)] overflow-hidden animate-[fadeIn_140ms_ease-out]"
                  style={{
                    animation: 'slashPaletteIn 140ms cubic-bezier(0.2, 0.8, 0.2, 1)',
                  }}
                >
                  <div className="px-3 pt-2.5 pb-1.5 flex items-center justify-between">
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600">
                      Commands
                    </span>
                    <span className="text-[10px] font-mono text-zinc-700">
                      ↑↓ navigate · ↵ select · esc close
                    </span>
                  </div>
                  <div className="px-1.5 pb-1.5">
                    {filteredCommands.map((cmd, i) => {
                      const Icon = cmd.icon;
                      const active = i === paletteIdx;
                      return (
                        <button
                          key={cmd.key}
                          data-testid={`palette-cmd-${cmd.key}`}
                          onMouseEnter={() => setPaletteIdx(i)}
                          onClick={() => selectCommand(cmd)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                            active
                              ? 'bg-amber-500/[0.09] text-amber-200'
                              : 'text-zinc-300 hover:bg-white/[0.03]'
                          }`}
                        >
                          <div
                            className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 border transition-colors ${
                              active
                                ? 'bg-amber-500/15 border-amber-500/30 text-amber-300'
                                : 'bg-white/[0.03] border-white/[0.06] text-zinc-400'
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`text-[13px] font-mono tracking-tight ${
                                active ? 'text-amber-200' : 'text-zinc-200'
                              }`}
                            >
                              {cmd.label}
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">{cmd.hint}</div>
                          </div>
                          {active && (
                            <span className="text-[10px] font-mono text-amber-400/70 tracking-widest">
                              ↵
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <textarea
                ref={inputRef}
                data-testid="chat-input"
                value={input}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                placeholder={
                  voiceMode
                    ? vmListening
                      ? (vmInterim || 'Listening…')
                      : isSpeaking
                        ? 'LEE is speaking…'
                        : 'One moment…'
                    : loading
                      ? 'LEE is thinking…'
                      : 'Message LEE…'
                }
                rows={1}
                disabled={loading}
                className="w-full bg-transparent border-0 outline-none resize-none px-4 sm:px-5 pt-4 pb-2 text-base sm:text-[15px] text-zinc-100 placeholder:text-zinc-600 leading-relaxed caret-amber-400"
                style={{ minHeight: '52px', maxHeight: '200px' }}
              />

              {/* Bottom row: actions + send */}
              <div className="flex items-center justify-between gap-1 px-2 sm:px-3 pb-2.5 pt-1 overflow-x-auto">
                <div className="flex items-center gap-0.5 sm:gap-1 min-w-0">
                  {/* Attach */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={onFilePick}
                    data-testid="file-input"
                  />
                  <button
                    data-testid="attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    title="Attach file"
                    className="p-2 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.05] transition disabled:opacity-40"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>

                  {/* Voice Mode — continuous hands-free conversation */}
                  <button
                    data-testid="voice-mode-btn"
                    onClick={toggleVoiceMode}
                    title={
                      voiceMode
                        ? 'Voice Mode: ON (click to stop)'
                        : 'Voice Mode: click to talk naturally with LEE'
                    }
                    aria-pressed={voiceMode}
                    className={`p-2 rounded-lg transition ${
                      voiceMode
                        ? vmListening
                          ? 'text-amber-300 bg-amber-500/15 animate-pulse'
                          : 'text-amber-400 bg-amber-500/10'
                        : 'text-zinc-500 hover:text-amber-300 hover:bg-white/[0.05]'
                    }`}
                  >
                    <Radio className="w-4 h-4" />
                  </button>

                  {/* Voice (press-to-talk transcription, fills input) */}
                  <button
                    data-testid="voice-btn"
                    onClick={toggleRecording}
                    title={recording ? 'Stop recording' : 'Voice input'}
                    className={`p-2 rounded-lg transition ${
                      recording
                        ? 'text-red-400 bg-red-500/10 animate-pulse'
                        : 'text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.05]'
                    }`}
                  >
                    {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>

                  {/* V1: Voice response toggle (persisted, default OFF) */}
                  <button
                    data-testid="speak-toggle-btn"
                    onClick={toggleSpeakResponses}
                    title={
                      speakResponses
                        ? 'Voice response: ON (click to mute LEE)'
                        : 'Voice response: OFF (click to let LEE speak replies)'
                    }
                    aria-pressed={speakResponses}
                    className={`p-2 rounded-lg transition ${
                      speakResponses
                        ? 'text-emerald-400 bg-emerald-500/10'
                        : 'text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.05]'
                    }`}
                  >
                    {speakResponses ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>

                  {/* V4: Stop Speaking — visible only while audio is playing */}
                  {isSpeaking && (
                    <button
                      data-testid="stop-speaking-btn"
                      onClick={stopSpeaking}
                      title="Stop speaking"
                      className="p-2 rounded-lg text-red-400 bg-red-500/10 hover:bg-red-500/20 transition animate-pulse"
                    >
                      <Square className="w-4 h-4" />
                    </button>
                  )}

                  <div className="w-px h-4 bg-white/[0.06] mx-1" />

                  {/* Quick actions */}
                  {quickActions.map((qa) => (
                    <button
                      key={qa.label}
                      data-testid={`quick-${qa.label.toLowerCase()}`}
                      onClick={() => {
                        setInput((prev) => (prev ? prev : qa.prompt));
                        inputRef.current?.focus();
                      }}
                      className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-zinc-500 hover:text-amber-300 hover:bg-white/[0.04] transition"
                    >
                      <qa.icon className="w-3 h-3" />
                      <span className="hidden sm:inline">{qa.label}</span>
                    </button>
                  ))}
                </div>

                {/* Send */}
                <button
                  data-testid="send-btn"
                  onClick={() => handleSend()}
                  disabled={!input.trim() || loading}
                  className="flex items-center justify-center h-9 w-9 rounded-lg bg-amber-500 hover:bg-amber-400 text-black disabled:bg-white/[0.05] disabled:text-zinc-600 transition-all"
                  title="Send"
                >
                  {loading ? (
                    <div className="w-3.5 h-3.5 border-[1.5px] border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <p className="hidden sm:block text-[10px] text-zinc-700 mt-2.5 text-center font-mono tracking-wide">
              Enter to send · Shift+Enter for newline · / for commands
            </p>
          </div>
        </div>
      </main>
    </div>
    </Layout>
  );
};

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

const AlertsBanner = () => {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/idea/digikey/alerts`, { headers: getAuthHeaders() }).catch(() => ({ data: { alerts: [] } })),
      axios.get(`${API}/credit/intel/alerts`, { headers: getAuthHeaders() }).catch(() => ({ data: { alerts: [] } })),
    ]).then(([dk, ci]) => {
      const merged = [
        ...(dk.data.alerts || []).map((a) => ({ ...a, _kind: 'sourcing' })),
        ...(ci.data.alerts || []).map((a) => ({ ...a, _kind: 'credit' })),
      ];
      setAlerts(merged);
    });
  }, []);
  const dismiss = async () => {
    try {
      await Promise.all([
        axios.post(`${API}/idea/digikey/alerts/seen`, {}, { headers: getAuthHeaders() }),
        axios.post(`${API}/credit/intel/alerts/seen`, {}, { headers: getAuthHeaders() }),
      ]);
    } catch (_e) { /* non-blocking */ }
    setAlerts([]);
  };
  const label = (a) => {
    if (a._kind === 'credit') {
      return { util_crossed: 'UTILIZATION OVER 30%', util_approaching: 'NEARING 30%', overdue: 'OVERDUE', new_account: 'NEW ACCOUNT' }[a.type] || 'CREDIT';
    }
    return { out_of_stock: 'OUT OF STOCK', price_jump: 'PRICE JUMP' }[a.type] || 'NOT FOUND';
  };
  if (!alerts.length) return null;
  return (
    <div
      data-testid="sourcing-alerts-banner"
      className="mb-6 rounded-xl border border-red-400/25 bg-red-500/[0.05] px-4 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-red-300/90 mb-1.5">
            Alerts · {alerts.length}
          </div>
          <div className="space-y-1">
            {alerts.slice(0, 3).map((a) => (
              <div key={a.id} className="text-[12px] text-zinc-300 truncate">
                <span className="text-red-300/80 font-mono">{label(a)}</span>
                {' — '}{a._kind === 'credit' ? a.detail : `${a.part_name}: ${a.detail}`}
              </div>
            ))}
            {alerts.length > 3 && (
              <div className="text-[11px] text-zinc-500">+{alerts.length - 3} more</div>
            )}
          </div>
        </div>
        <button
          data-testid="sourcing-alerts-dismiss"
          onClick={dismiss}
          className="text-[10px] font-mono uppercase text-zinc-500 hover:text-zinc-300 shrink-0"
        >
          dismiss
        </button>
      </div>
    </div>
  );
};

const EmptyState = ({ onPick }) => {
  const prompts = [
    'What should I be thinking about this week that I\'m not?',
    'Help me pressure-test a decision I\'m weighing.',
    'Turn a messy idea I have into a one-paragraph pitch.',
    'What\'s the highest-leverage move I can make in the next 24 hours?',
  ];

  // Goal Intelligence — morning brief
  const [brief, setBrief] = useState(null);
  // Phase E — Daily Executive Brief
  const [execBrief, setExecBrief] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/chat/morning-brief`, {
          headers: getAuthHeaders(),
        });
        if (!cancelled) setBrief(res.data);
      } catch (_e) {
        // silent — brief is non-blocking
      }
    };
    const loadExec = async () => {
      try {
        const res = await axios.get(`${API}/brief/executive`, {
          headers: getAuthHeaders(),
        });
        if (!cancelled) setExecBrief(res.data);
      } catch (_e) {
        // silent — executive brief is non-blocking
      }
    };
    load();
    loadExec();
    return () => { cancelled = true; };
  }, []);

  const hasGoals = brief && brief.has_goals;
  const overdue = brief?.overdue_count || 0;
  const dueSoon = brief?.due_soon_count || 0;
  const active = brief?.active_count || 0;
  const leverage = brief?.highest_leverage?.title;
  const nextStep = brief?.next_step;

  return (
    <div
      data-testid="empty-state"
      className="flex flex-col items-center justify-center py-6 sm:py-20 text-center"
    >
      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mb-4 sm:mb-7 shadow-[0_0_60px_-20px_rgba(245,158,11,0.5)]">
        <span className="text-lg font-bold font-mono text-amber-400">L</span>
      </div>

      {hasGoals && (
        <div
          data-testid="morning-brief"
          className="w-full max-w-[560px] mb-5 sm:mb-8 text-left rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 sm:px-5 sm:py-4"
        >
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-amber-400/80">
            <Compass className="w-3 h-3" />
            <span data-testid="brief-headline">
              {overdue > 0 && `${overdue} overdue · `}
              {dueSoon > 0 && `${dueSoon} due soon · `}
              {active} active goal{active !== 1 ? 's' : ''}
            </span>
          </div>
          {leverage && (
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1">
                Highest leverage move
              </div>
              <div
                data-testid="brief-leverage"
                className="text-[14px] text-zinc-100 leading-[1.55]"
              >
                {leverage}
              </div>
            </div>
          )}
          {nextStep && (
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1">
                Next step
              </div>
              <div
                data-testid="brief-next-step"
                className="text-[13px] text-zinc-400 leading-[1.55]"
              >
                {nextStep}
              </div>
            </div>
          )}
          {brief?.health?.has_data && (
            <div
              data-testid="brief-health"
              className="mt-4 pt-3 border-t border-white/[0.05] flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-zinc-400"
            >
              <span data-testid="brief-health-water" className="flex items-center gap-1.5">
                <span className="text-zinc-500">💧</span>
                <span>{brief.health.water_oz}/{brief.health.water_goal_oz} oz</span>
              </span>
              <span data-testid="brief-health-supplements" className="flex items-center gap-1.5">
                <span className="text-zinc-500">💊</span>
                <span>{brief.health.supplements_status}</span>
              </span>
              <span data-testid="brief-health-mood" className="flex items-center gap-1.5">
                <span className="text-zinc-500">🧠</span>
                <span>{brief.health.mood_line}</span>
              </span>
              {brief.health.open_symptoms_count > 0 && (
                <span data-testid="brief-health-symptoms" className="flex items-center gap-1.5 text-amber-400/80">
                  <span>🩹</span>
                  <span>{brief.health.open_symptoms_count} open</span>
                  {brief.health.stale_symptoms_count > 0 && (
                    <span className="text-amber-300">· {brief.health.stale_symptoms_count} stale</span>
                  )}
                </span>
              )}
            </div>
          )}
          <button
            data-testid="brief-focus-btn"
            onClick={() => onPick('/focus')}
            className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-amber-400/90 hover:text-amber-300 transition-colors font-mono uppercase tracking-[0.12em]"
          >
            Focus me <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}

      {execBrief && (
        <div
          data-testid="executive-brief"
          className="w-full max-w-[560px] mb-5 sm:mb-8 text-left rounded-xl border border-amber-500/[0.12] bg-amber-500/[0.03] px-4 py-3 sm:px-5 sm:py-4"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-amber-400/80">
              <span>📋</span>
              <span data-testid="exec-brief-day">Executive Brief · {execBrief.day}</span>
            </div>
            {(execBrief.risks || []).length > 0 && (
              <span
                data-testid="exec-brief-risk-count"
                className="text-[11px] font-mono text-amber-300/80"
              >
                ⚠ {execBrief.risks.length} risk{execBrief.risks.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {execBrief.headline && (
            <div
              data-testid="exec-brief-headline"
              className="mt-3 text-[14px] text-zinc-100 leading-[1.55] font-medium"
            >
              {execBrief.headline}
            </div>
          )}
          {(execBrief.priorities || []).length > 0 && (
            <div className="mt-3 hidden sm:block">
              <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1.5">
                Top priorities
              </div>
              <div data-testid="exec-brief-priorities" className="space-y-1">
                {execBrief.priorities.slice(0, 3).map((p, i) => (
                  <div key={i} className="flex items-start gap-2 text-[13px] text-zinc-300 leading-[1.5]">
                    <span className="text-amber-400/70 font-mono text-[11px] mt-[2px]">{i + 1}</span>
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(execBrief.wealth_recommendations || []).length > 0 && (
            <div className="mt-3 hidden sm:block">
              <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1.5">
                Wealth · live data
              </div>
              <div data-testid="exec-brief-wealth-recs" className="space-y-1">
                {execBrief.wealth_recommendations.slice(0, 3).map((r, i) => (
                  <div key={i} className="text-[13px] text-zinc-300 leading-[1.5]">{r}</div>
                ))}
              </div>
            </div>
          )}
          {(execBrief.opportunities?.top_scoring || []).length > 0 && (
            <div className="mt-3 hidden sm:block">
              <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1">
                Top opportunity
              </div>
              <div data-testid="exec-brief-top-opportunity" className="text-[13px] text-zinc-300 leading-[1.5]">
                {execBrief.opportunities.top_scoring[0].title?.slice(0, 80)}
                <span className="text-amber-400/80 font-mono text-[12px] ml-2">
                  score {execBrief.opportunities.top_scoring[0].opportunity_score}
                </span>
                {execBrief.opportunities.new_today > 0 && (
                  <span className="text-zinc-500 text-[12px] ml-2">
                    · {execBrief.opportunities.new_today} new today
                  </span>
                )}
              </div>
            </div>
          )}
          <button
            data-testid="exec-brief-full-btn"
            onClick={() => onPick('/brief')}
            className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-amber-400/90 hover:text-amber-300 transition-colors font-mono uppercase tracking-[0.12em]"
          >
            Full brief <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}

      <h1 className="text-[22px] sm:text-[32px] font-semibold text-white tracking-tight leading-tight">
        What do you want to think through?
      </h1>
      <p className="text-zinc-500 text-[14px] mt-3 max-w-md">
        Sharp mind. Natural voice. Zero pretension.
      </p>

      <div className="mt-6 sm:mt-12 grid grid-cols-2 gap-2 sm:gap-2.5 w-full max-w-[560px]">
        {prompts.map((p, i) => (
          <button
            key={i}
            data-testid={`starter-${i}`}
            onClick={() => onPick(p)}
            className="text-left px-3 py-2.5 text-[12px] sm:px-4 sm:py-3.5 sm:text-[13px] text-zinc-400 bg-white/[0.025] hover:bg-white/[0.05] border border-white/[0.06] hover:border-amber-500/25 hover:text-zinc-200 rounded-xl transition-all"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
};

const ChatImage = ({ fileId, name, imageUrls, setImageUrls }) => {
  const url = imageUrls?.[fileId];
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (url || failed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/files/${fileId}/raw`, {
          headers: getAuthHeaders(),
          responseType: 'blob',
        });
        const obj = URL.createObjectURL(res.data);
        if (!cancelled && setImageUrls) setImageUrls((prev) => ({ ...prev, [fileId]: obj }));
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [url, failed, fileId, setImageUrls]);

  if (failed) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/[0.05] border border-white/10 text-[12px] text-zinc-400 font-mono" data-testid="user-image-fallback">
        <PaperclipIcon className="w-3 h-3" /> {name}
      </span>
    );
  }
  if (!url) {
    return <div className="w-40 h-28 rounded-xl bg-white/[0.04] border border-white/10 animate-pulse" data-testid="user-image-loading" />;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block">
      <img
        src={url}
        alt={name}
        data-testid="user-uploaded-image"
        className="rounded-xl border border-white/[0.08] max-w-full max-h-[320px] shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)]"
        loading="lazy"
      />
    </a>
  );
};

const MessageBubble = ({
  role,
  content,
  streaming = false,
  onReplay,
  isSpeaking,
  stopSpeaking,
  imageUrls,
  setImageUrls,
}) => {
  const [copied, setCopied] = useState(false);
  const [replayBusy, setReplayBusy] = useState(false);

  if (role === 'user') {
    const segments = [];
    const re = new RegExp(IMAGE_TOKEN.source, 'g');
    let last = 0;
    let mt;
    let k = 0;
    const raw = content || '';
    while ((mt = re.exec(raw)) !== null) {
      if (mt.index > last) segments.push({ kind: 'text', text: raw.slice(last, mt.index), key: k++ });
      segments.push({ kind: 'image', fileId: mt[1], name: mt[2], key: k++ });
      last = mt.index + mt[0].length;
    }
    if (last < raw.length) segments.push({ kind: 'text', text: raw.slice(last), key: k++ });
    return (
      <div data-testid="user-message" className="flex justify-end">
        <div className="max-w-[78%] rounded-2xl rounded-br-md bg-zinc-800/90 border border-white/[0.06] px-4 py-3 shadow-[0_1px_0_rgba(255,255,255,0.02)_inset] space-y-2">
          {segments.map((s) =>
            s.kind === 'image' ? (
              <ChatImage key={s.key} fileId={s.fileId} name={s.name} imageUrls={imageUrls} setImageUrls={setImageUrls} />
            ) : (
              s.text.trim() && (
                <p key={s.key} className="text-[15px] text-zinc-100 leading-[1.65] whitespace-pre-wrap">
                  {s.text.replace(/^\n+|\n+$/g, '')}
                </p>
              )
            )
          )}
        </div>
      </div>
    );
  }
  if (role === 'error') {
    return (
      <div data-testid="error-message" className="flex justify-start">
        <div className="max-w-[78%] rounded-2xl rounded-bl-md bg-red-500/[0.06] border border-red-500/20 px-4 py-3">
          <p className="text-[14px] text-red-300">{content}</p>
        </div>
      </div>
    );
  }

  const handleReplay = async () => {
    if (!onReplay || !content || replayBusy) return;
    // If LEE is currently speaking, the click stops him instead.
    if (isSpeaking && stopSpeaking) {
      stopSpeaking();
      return;
    }
    setReplayBusy(true);
    try {
      await onReplay(content);
    } finally {
      setReplayBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* noop */
    }
  };

  return (
    <div data-testid="assistant-message" className="flex justify-start group">
      <div className="flex items-start gap-3 max-w-[82%] min-w-0">
        <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-[11px] font-bold font-mono text-amber-400">L</span>
        </div>
        <div className="flex flex-col gap-1.5 min-w-0 max-w-full">
          <div className="rounded-2xl rounded-tl-md bg-white/[0.03] border border-white/[0.055] px-5 py-4 min-w-[60px] max-w-full overflow-hidden">
            <div className="space-y-0">
              {renderMessage(content)}
              {streaming && (
                <span className="inline-block w-[7px] h-[15px] bg-amber-400/80 ml-0.5 align-middle animate-pulse rounded-sm" />
              )}
            </div>
          </div>
          {!streaming && (
            <div className="flex items-center gap-1.5 pl-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
              <button
                data-testid="replay-msg-btn"
                onClick={handleReplay}
                disabled={replayBusy}
                title={isSpeaking ? 'Stop' : 'Replay aloud'}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-mono uppercase tracking-[0.12em] text-zinc-500 hover:text-amber-300 hover:bg-white/[0.04] transition-colors disabled:opacity-50"
              >
                {isSpeaking ? (
                  <>
                    <VolumeX className="w-3 h-3" />
                    <span>Stop</span>
                  </>
                ) : replayBusy ? (
                  <>
                    <Volume2 className="w-3 h-3 animate-pulse" />
                    <span>Loading…</span>
                  </>
                ) : (
                  <>
                    <Volume2 className="w-3 h-3" />
                    <span>Replay</span>
                  </>
                )}
              </button>
              <button
                data-testid="copy-msg-btn"
                onClick={handleCopy}
                title="Copy text"
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-mono uppercase tracking-[0.12em] text-zinc-500 hover:text-amber-300 hover:bg-white/[0.04] transition-colors"
              >
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ThinkingIndicator = () => (
  <div data-testid="thinking-indicator" className="flex justify-start">
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mt-0.5">
        <span className="text-[11px] font-bold font-mono text-amber-400">L</span>
      </div>
      <div className="rounded-2xl rounded-tl-md bg-white/[0.03] border border-white/[0.055] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400/80 animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400/60 animate-bounce" style={{ animationDelay: '140ms' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400/40 animate-bounce" style={{ animationDelay: '280ms' }} />
          </div>
          <span className="text-[12px] text-zinc-500 font-mono tracking-wide">Thinking…</span>
        </div>
      </div>
    </div>
  </div>
);

export default TranscendentChat;
